
import React, { useState, useCallback, useEffect, useMemo, Suspense, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import LandingPage from './components/LandingPage';
import { brand } from './whitelabel';
import { Toast, Spinner } from './components/common/UIComponents';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { auth } from './firebase';
import { signInWithEmailAndPassword, setPersistence, browserLocalPersistence, browserSessionPersistence, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';

// Helper to retry lazy imports on chunk load failure (fixes "Failed to fetch dynamically imported module")
const lazyRetry = (importFunc: () => Promise<any>) => {
  return React.lazy(async () => {
    try {
      return await importFunc();
    } catch (error: any) {
      console.error("Lazy load error:", error);
      // Check for chunk load errors
      if (error.toString().includes('Failed to fetch') || error.toString().includes('Importing a module script failed')) {
        const storageKey = `retry-${window.location.pathname}`;
        if (!sessionStorage.getItem(storageKey)) {
          sessionStorage.setItem(storageKey, 'true');
          window.location.reload(); // Force reload to fetch new index.html with correct hashes
          return { default: () => <div className="h-screen flex items-center justify-center text-slate-500">Updating application version...</div> };
        }
      }
      throw error;
    }
  });
};

// "kca-main" is the original, pre-multi-tenant school — many of its real historical documents
// predate the `schoolId` field entirely. Firestore's `==` operator never matches a missing
// field, so a strict where('schoolId','==','kca-main') query silently returns NOTHING for
// those legacy docs, even though the data genuinely exists (this is why the admin dashboard
// could show 0 students/invoices/etc. for a school that's actually been active for years).
// Every other school was created after schoolId existed, so its documents are safe to filter
// strictly. This fetches the whole collection for kca-main (keeping docs whose schoolId is
// missing OR already 'kca-main') and the normal scoped query for every other school.
const subscribeSchoolScoped = <T,>(
  schoolId: string,
  col: string,
  onData: (items: T[]) => void,
  onError?: (err: any) => void
) => {
  const isMain = schoolId === 'kca-main';
  const ref = isMain
    ? query(collection(db, col))
    : query(collection(db, col), where('schoolId', '==', schoolId));

  return onSnapshot(ref, (snap) => {
    const items = snap.docs
      .map(d => ({ id: d.id, ...d.data() } as any))
      .filter(d => !isMain || !d.schoolId || d.schoolId === 'kca-main') as unknown as T[];
    onData(items);
  }, onError);
};

// Lazy load portals with retry logic
const AdminPortal = lazyRetry(() => import('./components/portals/AdminPortal'));
const TeacherPortal = lazyRetry(() => import('./components/portals/TeacherPortal'));
const StudentPortal = lazyRetry(() => import('./components/portals/StudentPortal'));
const NonAcademicPortal = lazyRetry(() => import('./components/portals/NonAcademicPortal').then(module => ({ default: module.NonAcademicPortal })));
const EQAPortal = lazyRetry(() => import('./components/portals/EQAPortal'));
const PublicEnrollment = lazyRetry(() => import('./components/public/PublicEnrollment'));
const StudentCBT = lazyRetry(() => import('./components/portals/StudentCBT'));
const CBTManager = lazyRetry(() => import('./components/portals/CBTManager'));
const SuperAdminDashboard = lazyRetry(() => import('./components/portals/SuperAdminDashboard'));
const ExternalStudentPortal = lazyRetry(() => import('./components/portals/ExternalStudentPortal'));
const PublicQuizView = lazyRetry(() => import('./components/public/PublicQuizView'));
const SharedMaterialView = lazyRetry(() => import('./components/public/SharedMaterialView'));
const PublicActivityView = lazyRetry(() => import('./components/public/PublicActivityView'));
const GuestClassroomView = lazyRetry(() => import('./components/public/GuestClassroomView'));
const SideCamera = lazyRetry(() => import('./components/portals/musabaqa/qualifier/SideCamera'));
const BroadcastView = lazyRetry(() => import('./components/portals/musabaqa/BroadcastView'));
const PublicAdmissionForm = lazyRetry(() => import('./components/public/PublicAdmissionForm'));
const PublicSummerProgramForm = lazyRetry(() => import('./components/public/PublicSummerProgramForm'));

import { Portal, User, Review, SchoolClass, Student, Section, TeacherAccount, PublishedResult, IQASubmission, IQAFormType, AttendanceSubmission, Announcement, Invoice, Payment, Payroll, ExpenseClaim, FeeCategory, FeeTemplate, SchoolSettings, Discount, SalaryAdvanceRequest, InvoiceItem, NonAcademicStaffAccount, IssueReport, ClockInRecord, IssueStatus, AttendanceStatus, IQASamplingPlanEntry, Observation, ActionPlanItem, ActionPlanItemComment, StandardisationFile, StudentFeedback, FeedbackStatus, ClassEnrollment, Subject, CBT_Test, StandardizationMeeting, Archive, AdminAccount, Notification, AdmissionApplication, TeacherApplication, SummerProgram, SummerEnrollment, SummerProgramSignup, TransferRequest, ArchivedStudent } from './types';

import { collection, onSnapshot, query, doc, setDoc, addDoc, updateDoc, deleteDoc, getDoc, writeBatch, getDocs, where, documentId, increment, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { generateInvoices as generateTermInvoices } from './services/finance/ledger';
import { schoolInitials, termNumber } from './services/finance/money';
import { registerUser, createFirebaseAuthUser } from './services/authService';
import { db } from './firebase';
import { generateSessions, getCurrentSession, addWorkingDays } from './utils';
import { computeGrade, needsResit, scholarshipThreshold, isResitSubject, normalizeSubjectName } from './utils/academicPolicy';
import { matchesPublishedResult } from './utils/resultMatching';

// Utility to strip undefined values for Firestore
const cleanData = (data: any): any => {
  if (data === null || data === undefined) return null;
  if (Array.isArray(data)) return data.map(cleanData);
  if (typeof data === 'object' && !(data instanceof Date)) {
    return Object.entries(data).reduce((acc, [key, value]) => {
      if (value !== undefined) acc[key] = cleanData(value);
      return acc;
    }, {} as any);
  }
  return data;
};

import { useSchool } from './contexts/SchoolContext';
import { SubscriptionGate } from './components/SubscriptionGate';
const DemoBanner = React.lazy(() => import('./components/demo/DemoBanner'));

const withTimeout = <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> => {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => {
      console.warn(`Promise timed out after ${ms}ms. Returning fallback.`);
      resolve(fallback);
    }, ms);
    promise.then(
      (res) => {
        clearTimeout(timer);
        resolve(res);
      },
      (err) => {
        clearTimeout(timer);
        console.error("Promise failed:", err);
        resolve(fallback);
      }
    );
  });
};

const emptyQuerySnapshot = { empty: true, docs: [] } as any;

// Fallback resolver for students whose Firestore doc can't be matched by the normal
// email-based query (e.g. a legacy record with a missing/mismatched email field). This
// used to run as a raw client-side getDocs(collection(db,'students')) with NO filter
// at all — every school, every student. That only ever worked by accident: firestore
// .rules' students/{id} read is scoped to isOwnRecord()/getSchoolId(), and the caller
// this fallback exists FOR, by definition, doesn't resolve via either (their stored
// email doesn't match, and they aren't found in any collection yet) — so an unfiltered
// `list` gets every candidate denied and Firestore fails the whole request. Needs a
// real schoolId to search within (Admin SDK, server-side, bypasses rules for this one
// narrow lookup) — see functions/src/index.ts's findStudentByNameFallback.
const findStudentFallback = async (email: string, schoolId: string | undefined): Promise<Student | null> => {
  if (!schoolId) return null;
  try {
    const fn = httpsCallable(getFunctions(), 'findStudentByNameFallback');
    const result: any = await withTimeout(fn({ email, schoolId }), 30000, { data: { student: null } });
    return result?.data?.student || null;
  } catch (err) {
    console.error("Error in findStudentFallback:", err);
    return null;
  }
};

const App: React.FC = () => {
  const { currentSchool, schoolSettings: contextSchoolSettings, loading: schoolLoading } = useSchool();
  const [activePortal, setActivePortal] = useState<Portal>(Portal.LANDING);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  // Display name of whoever is currently signed in, regardless of role — used to
  // stamp "who did this" onto resit audit-trail fields. currentUser.data holds the
  // role-specific profile (TeacherAccount/AdminAccount), neither of which is typed
  // on the base User shape. Declared early since several handlers below close over
  // it in their useCallback dependency arrays.
  const getActingUserName = useCallback(() => {
    const data = currentUser?.data as { fullName?: string; username?: string } | undefined;
    return data?.fullName || data?.username || currentUser?.email || 'Unknown';
  }, [currentUser]);
  const [loading, setLoading] = useState(true);
  // Prevents onAuthStateChanged from running duplicate Firestore queries when handleLogin is already in progress
  const isHandlingLoginRef = useRef(false);
  // Track who is shown, so we notice if the Firebase sign-in ends underneath an open portal
  // (e.g. logged out in another tab) instead of failing quietly on every AI/database call.
  const currentUserRef = useRef<User | null>(null);
  const manualLogoutRef = useRef(false);
  useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);

  // Emergency session restoration timeout fallback to ensure page load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        console.warn("Session restoration timed out. Forcing loading state to false.");
        setLoading(false);
      }
    }, 3500); // 3.5 seconds maximum emergency timeout
    return () => clearTimeout(timer);
  }, [loading]);

  // --- AUTH PERSISTENCE ---
  useEffect(() => {
    // Prefer localStorage persistence; fall back to sessionStorage on iOS
    // private browsing where localStorage has a 0-byte quota.
    setPersistence(auth, browserLocalPersistence)
      .catch(() => setPersistence(auth, browserSessionPersistence))
      .catch((error) => {
        console.error("Auth Persistence Error:", error);
      });

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (!firebaseUser && currentUserRef.current && !manualLogoutRef.current) {
        setCurrentUser(null);
        setActivePortal(Portal.LANDING);
        setToast({ message: 'Your sign-in ended (for example, you logged out in another tab). Please sign in again — your saved work is safe.', type: 'error' });
      }
      manualLogoutRef.current = false;
      if (firebaseUser?.email) {
        console.log("Restoring session for:", firebaseUser.email);
        const userEmail = firebaseUser.email;
        const inputEmail = userEmail.toLowerCase();

        // 1. Hardcoded Admins
        if (inputEmail === 'sadmin@kdcambridge.com') {
          setCurrentUser({
            id: 'admin-user',
            role: 'super_admin',
            email: userEmail,
            permissions: ['manage_students', 'manage_teachers', 'manage_classes', 'manage_finance', 'manage_timetable', 'manage_cbt', 'manage_settings', 'manage_admins', 'manage_non_academic']
          });
          setActivePortal(Portal.SUPER_ADMIN);
          setLoading(false);
          return;
        }

        if (inputEmail === 'admin@kdcambridge.com') {
          setCurrentUser({
            id: 'school-admin-main',
            role: 'admin',
            email: userEmail,
            permissions: ['manage_students', 'manage_teachers', 'manage_classes', 'manage_finance', 'manage_timetable', 'manage_fees', 'manage_reports', 'manage_settings'],
            schoolId: 'kca-main'
          });
          setActivePortal(Portal.ADMIN);
          setLoading(false);
          return;
        }

        // Skip if handleLogin is already resolving this (prevents 8 simultaneous queries on explicit login)
        if (isHandlingLoginRef.current) {
          setLoading(false);
          return;
        }

        // 2. Fast path: cached session hint (skips 4 parallel queries on repeat visits)
        const HINT_KEY = 'kca_session_hint';
        const saveHint = (col: string, docId: string) => {
          try { localStorage.setItem(HINT_KEY, JSON.stringify({ uid: firebaseUser.uid, col, docId })); } catch {}
        };

        try {
          const rawHint = localStorage.getItem(HINT_KEY);
          if (rawHint) {
            const hint = JSON.parse(rawHint);
            if (hint?.uid === firebaseUser.uid && hint.col && hint.docId) {
              const snap = await withTimeout(
                getDoc(doc(db, hint.col, hint.docId)),
                3000,
                null
              );
              if (snap && snap.exists()) {
                const d = snap.data();
                if (hint.col === 'students') {
                  const s = { id: snap.id, ...d } as Student;
                  setCurrentUser({ id: s.id, email: userEmail, role: 'student', data: s, schoolId: s.schoolId });
                  setActivePortal((s as any).isExternal ? Portal.EXTERNAL_STUDENT : Portal.STUDENT);
                  setLoading(false); return;
                } else if (hint.col === 'teachers') {
                  const t = { id: snap.id, ...d };
                  setCurrentUser({ id: (t as any).username || snap.id, role: 'teacher', email: userEmail, data: t, schoolId: (t as any).schoolId || 'kca-main' });
                  setActivePortal(Portal.TEACHER);
                  setLoading(false); return;
                } else if (hint.col === 'admins') {
                  const a = { id: snap.id, ...d } as AdminAccount;
                  if (a.active) {
                    setCurrentUser({ id: a.username, role: 'admin', email: userEmail, permissions: a.permissions, schoolId: a.schoolId });
                    setActivePortal(Portal.ADMIN);
                    setLoading(false); return;
                  }
                } else if (hint.col === 'non_academic_staff') {
                  const st = { id: snap.id, ...d } as unknown as NonAcademicStaffAccount;
                  setCurrentUser({ id: st.username || snap.id, role: 'non-academic', email: userEmail, data: st, schoolId: st.schoolId });
                  setActivePortal(Portal.NON_ACADEMIC);
                  setLoading(false); return;
                }
              }
            }
          }
        } catch { /* stale cache — fall through to full query */ }

        // 3. Full parallel query (first login or cache miss)
        try {
          const searchEmails = Array.from(new Set([inputEmail, userEmail]));
          const adminQuery = query(collection(db, 'admins'), where('email', 'in', searchEmails));
          const teacherQuery = query(collection(db, 'teachers'), where('email', 'in', searchEmails));
          const studentQuery = query(collection(db, 'students'), where('email', 'in', searchEmails));
          const staffQuery = query(collection(db, 'non_academic_staff'), where('email', 'in', searchEmails));

          const [adminSnap, teacherSnap, studentSnap, staffSnap] = await withTimeout(
            Promise.all([
              getDocs(adminQuery),
              getDocs(teacherQuery),
              getDocs(studentQuery),
              getDocs(staffQuery)
            ]),
            3500,
            [emptyQuerySnapshot, emptyQuerySnapshot, emptyQuerySnapshot, emptyQuerySnapshot]
          );

          let matchedUser: User | null = null;
          let matchedPortal: Portal | null = null;
          let matchedCollection: string | null = null;
          let matchedDocId: string | null = null;

          // 1. Check Admins
          if (!adminSnap.empty) {
            const adminData = { id: adminSnap.docs[0].id, ...adminSnap.docs[0].data() } as AdminAccount;
            if (adminData.active) {
              matchedUser = { id: adminData.username, role: 'admin', email: userEmail, permissions: adminData.permissions, schoolId: adminData.schoolId };
              matchedPortal = Portal.ADMIN;
              matchedCollection = 'admins';
              matchedDocId = adminSnap.docs[0].id;
            }
          }

          // 2. Check Teachers
          if (!matchedUser) {
            let teacherData: any = null;
            if (!teacherSnap.empty) {
              teacherData = { id: teacherSnap.docs[0].id, ...teacherSnap.docs[0].data() };
            } else if (userEmail.includes('@kdcambridge.com')) {
              const uname = userEmail.split('@')[0];
              const ids = Array.from(new Set([uname, uname.toLowerCase(), uname.charAt(0).toUpperCase() + uname.slice(1).toLowerCase()]));
              const snaps = await withTimeout(
                Promise.all(ids.map(id => getDoc(doc(db, 'teachers', id)))),
                3000,
                ids.map(() => null as any)
              );
              const found = snaps.find(s => s && s.exists());
              if (found) teacherData = { id: found.id, ...found.data() };
            }

            if (teacherData) {
              matchedUser = {
                id: teacherData.username || teacherData.id,
                role: 'teacher',
                email: userEmail,
                data: teacherData,
                schoolId: teacherData.schoolId || 'kca-main'
              };
              matchedPortal = Portal.TEACHER;
              matchedCollection = 'teachers';
              matchedDocId = teacherData.id;

              // Self-healing: Update missing email field in Firestore
              if (!teacherData.email) {
                console.log(`Self-healing (Restore): Writing email for teacher: ${teacherData.id}`);
                updateDoc(doc(db, 'teachers', teacherData.id), { email: userEmail }).catch((err) => {
                  console.error("Self-healing error updating teacher email:", err);
                });
              }
            }
          }

          // 3. Check Students
          if (!matchedUser) {
            let studentData: Student | null = null;
            if (!studentSnap.empty) {
              studentData = { id: studentSnap.docs[0].id, ...studentSnap.docs[0].data() } as Student;
            } else {
              studentData = await findStudentFallback(userEmail, currentSchool?.id);
            }

            if (studentData) {
              matchedUser = { id: studentData.id, email: userEmail, role: 'student', data: studentData, schoolId: studentData.schoolId };
              matchedPortal = studentData.isExternal ? Portal.EXTERNAL_STUDENT : Portal.STUDENT;
              matchedCollection = 'students';
              matchedDocId = studentData.id;

              // Self-healing: Update missing email field in Firestore
              if (!studentData.email) {
                console.log(`Self-healing (Restore): Writing email for student: ${studentData.id}`);
                updateDoc(doc(db, 'students', studentData.id), { email: userEmail }).catch((err) => {
                  console.error("Self-healing error updating student email:", err);
                });
              }
            }
          }

          // 4. Check Non-Academic Staff
          if (!matchedUser && !staffSnap.empty) {
            const staffData = { id: staffSnap.docs[0].id, ...staffSnap.docs[0].data() } as unknown as NonAcademicStaffAccount;
            const staffId = staffData.username || staffSnap.docs[0].id;
            matchedUser = { id: staffId, role: 'non-academic', email: userEmail, data: staffData, schoolId: staffData.schoolId };
            matchedPortal = Portal.NON_ACADEMIC;
            matchedCollection = 'non_academic_staff';
            matchedDocId = staffSnap.docs[0].id;
          }

          // Set state if matched
          if (matchedUser && matchedPortal) {
            setCurrentUser(matchedUser);
            setActivePortal(matchedPortal);
            if (matchedCollection && matchedDocId) {
              saveHint(matchedCollection, matchedDocId);
            }
          } else {
            setToast({ message: 'Profile not found for this account.', type: 'error' });
          }

        } catch (e) {
          console.error("Error restoring session:", e);
        }
      }
      // Always stop loading after check (whether user found or not)
      // Note: If user is NOT found, we stay on LANDING portal (default)
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  useEffect(() => {
    if (window.location.pathname === '/eqa-view') {
      setTimeout(() => {
        const code = prompt("Enter EQA Access Code:");
        if (code === "KCA-EQA-2024") {
          setActivePortal(Portal.EQA);
        } else {
          alert("Invalid Access Code");
          window.location.href = "/";
        }
      }, 500);
    }
  }, []);

  // --- FIRESTORE STATE ---
  const [schoolSettings, setSchoolSettings] = useState<SchoolSettings>({
    schoolName: brand.defaultSchoolName,
    schoolAddress: brand.defaultSchoolAddress,
    schoolEmail: brand.supportEmail,
    schoolPhone: brand.supportPhone,
    bankAccounts: [],
    logoDataUrl: '',
    schoolStampUrl: '',
    headOfSchoolSignatureUrl: '',
  });

  useEffect(() => {
    if (contextSchoolSettings) {
      setSchoolSettings((prev) => ({
        ...contextSchoolSettings,
        // Ensure critical fields are populated if missing in settings but available on school object
        logoDataUrl: contextSchoolSettings.logoDataUrl || (currentSchool as any)?.logoUrl || prev.logoDataUrl,
        schoolName: contextSchoolSettings.schoolName || (currentSchool as any)?.name || prev.schoolName
      }));
    } else if (currentSchool) {
      // Fallback for new schools without specific settings doc yet
      setSchoolSettings((prev) => ({
        ...prev,
        schoolName: currentSchool.name || prev.schoolName,
        schoolEmail: currentSchool.contactEmail || prev.schoolEmail,
        schoolAddress: currentSchool.address || prev.schoolAddress,
        logoDataUrl: (currentSchool as any)?.logoUrl || prev.logoDataUrl
      }));
    }
  }, [contextSchoolSettings, currentSchool]);

  // Sync School Context with User
  const { switchSchool } = useSchool();
  useEffect(() => {
    if (currentUser?.schoolId && currentUser.schoolId !== currentSchool?.id) {
      console.log("Switching school context to:", currentUser.schoolId);
      switchSchool(currentUser.schoolId);
    }
  }, [currentUser, currentSchool]);

  const [students, setStudents] = useState<Student[]>([]);
  const [teachers, setTeachers] = useState<TeacherAccount[]>([]);
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [sections, setSections] = useState<Section[]>([]);

  const handleSectionsLoaded = useCallback((fetchedSections: Section[]) => {
    setSections(fetchedSections);

    // Clean up duplicate auto-seeded docs if another section with the same core name exists in Firestore
    if (fetchedSections.some(s => s.id === 'sec_islamiyya') && fetchedSections.some(s => s.id !== 'sec_islamiyya' && s.name.toLowerCase().includes('islamiyya'))) {
      deleteDoc(doc(db, 'sections', 'sec_islamiyya')).catch(() => {});
    }
    if (fetchedSections.some(s => s.id === 'sec_tahfiz') && fetchedSections.some(s => s.id !== 'sec_tahfiz' && s.name.toLowerCase().includes('tahfiz'))) {
      deleteDoc(doc(db, 'sections', 'sec_tahfiz')).catch(() => {});
    }
    if (fetchedSections.some(s => s.id === 'sec_conventional') && fetchedSections.some(s => s.id !== 'sec_conventional' && s.name.toLowerCase().includes('conventional'))) {
      deleteDoc(doc(db, 'sections', 'sec_conventional')).catch(() => {});
    }

    const hasConventional = fetchedSections.some(s => (s.name || '').toLowerCase().includes('conventional') || s.id === 'BnFcZSf3IWg4NMJ6SruY');
    if (!hasConventional && currentSchool?.id && (currentUser?.role === 'admin' || currentUser?.role === 'super_admin')) {
      try {
        console.log(`Self-healing: Auto-seeding section "Conventional" for school ${currentSchool.id}`);
        setDoc(doc(db, 'sections', 'BnFcZSf3IWg4NMJ6SruY'), { id: 'BnFcZSf3IWg4NMJ6SruY', name: 'Conventional', schoolId: currentSchool.id });
      } catch (e) {
        console.error('Error auto-seeding section Conventional:', e);
      }
    }
  }, [currentSchool, currentUser]);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [publishedResults, setPublishedResults] = useState<PublishedResult[]>([]);
  const [attendanceSubmissions, setAttendanceSubmissions] = useState<AttendanceSubmission[]>([]);
  const [iqaSubmissions, setIqaSubmissions] = useState<IQASubmission[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [studentFeedback, setStudentFeedback] = useState<StudentFeedback[]>([]);
  const [nonAcademicStaff, setNonAcademicStaff] = useState<NonAcademicStaffAccount[]>([]);
  const [issueReports, setIssueReports] = useState<IssueReport[]>([]);
  const [clockInRecords, setClockInRecords] = useState<ClockInRecord[]>([]);
  const [payrolls, setPayrolls] = useState<Payroll[]>([]);
  const [salaryAdvances, setSalaryAdvances] = useState<SalaryAdvanceRequest[]>([]);
  const [expenseClaims, setExpenseClaims] = useState<ExpenseClaim[]>([]);
  const [observations, setObservations] = useState<Observation[]>([]);
  const [actionPlanItems, setActionPlanItems] = useState<ActionPlanItem[]>([]);
  const [standardisationFiles, setStandardisationFiles] = useState<StandardisationFile[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [weeklyPerformances, setWeeklyPerformances] = useState<any[]>([]);
  const [transferRequests, setTransferRequests] = useState<TransferRequest[]>([]);
  const [archivedStudents, setArchivedStudents] = useState<ArchivedStudent[]>([]);
  // No live single-doc subscription exists for a logged-in student's own record today —
  // `student` at the Portal.STUDENT render site otherwise falls back to the static
  // currentUser.data snapshot taken at login, which would never reflect an admin's
  // transfer approval (or the deadline countdown) without a re-login.
  const [liveStudentDoc, setLiveStudentDoc] = useState<Student | null>(null);

  // Messaging Global State
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  // Global Listener for Unread Messages
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', currentUser.id)
      // orderBy deleted to match MessagingSystem fix
    );

    const unsub = onSnapshot(q, (snap) => {
      let totalUnread = 0;
      snap.docs.forEach(doc => {
        const data = doc.data();
        const count = data.unreadCounts?.[currentUser.id] || 0;
        totalUnread += count;
      });
      setUnreadMessageCount(totalUnread);
    });

    return () => unsub();
  }, [currentUser]);
  const [cbtTests, setCbtTests] = useState<CBT_Test[]>([]);
  const [standardizationMeetings, setStandardizationMeetings] = useState<StandardizationMeeting[]>([]);
  const [archives, setArchives] = useState<Archive[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [admissionApplications, setAdmissionApplications] = useState<AdmissionApplication[]>([]);
  const [teacherApplications, setTeacherApplications] = useState<TeacherApplication[]>([]);

  // --- FINANCE & IQA STATE (Migrated from Local Storage) ---
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [feeTemplates, setFeeTemplates] = useState<FeeTemplate[]>([]);
  const [summerPrograms, setSummerPrograms] = useState<SummerProgram[]>([]);
  const [summerEnrollments, setSummerEnrollments] = useState<SummerEnrollment[]>([]);
  const [summerProgramSignups, setSummerProgramSignups] = useState<SummerProgramSignup[]>([]);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [iqaSamplingPlan, setIqaSamplingPlan] = useState<IQASamplingPlanEntry[]>([]);

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // --- FIRESTORE SUBSCRIPTIONS ---
  const [loadStage, setLoadStage] = useState(0); // 0=Init, 1=Critical, 2=Core, 3=Background

  // --- FIRESTORE SUBSCRIPTIONS ---

  // STAGE 1: DATA SUBSCRIPTIONS
  // Portal is unblocked immediately after school + user are confirmed.
  // All collection subscriptions run in the background and populate state as they arrive.
  useEffect(() => {
    if (schoolLoading) return;
    if (!currentSchool || !currentUser) {
      setLoading(false);
      return;
    }

    const q = (col: string) => query(collection(db, col), where('schoolId', '==', currentSchool.id));

    // Unblock the portal immediately — don't wait for large collection downloads
    setLoading(false);
    setLoadStage(1);

    const handleError = (collectionName: string, error: any) => {
      console.error(`Error fetching ${collectionName}:`, error);
      if (error.code === 'permission-denied') {
        setToast({ message: `Access Denied: ${collectionName}. Check Security Rules.`, type: 'error' });
      }
    };

    // For students: fetch the minimal data they need (invoices, classes, sections, teachers for Tahfiz recitation submission)
    if (currentUser.role === 'student') {
      const unsubInvoices = subscribeSchoolScoped<Invoice>(currentSchool.id, 'invoices', setInvoices, (err) => console.error(err));
      const unsubClasses = subscribeSchoolScoped<SchoolClass>(currentSchool.id, 'classes', setClasses, (err) => console.error(err));
      const unsubSections = subscribeSchoolScoped<Section>(currentSchool.id, 'sections', handleSectionsLoaded, (err) => console.error(err));
      const unsubTeachers = subscribeSchoolScoped<TeacherAccount>(currentSchool.id, 'teachers', setTeachers, (err) => console.error('teachers (student):', err));
      // Live view of the student's own record — currentUser.data is a static snapshot
      // from login, which would never reflect an admin's transfer approval without this.
      const unsubOwnStudent = onSnapshot(doc(db, 'students', currentUser.id), (snap) => {
        setLiveStudentDoc(snap.exists() ? ({ id: snap.id, ...snap.data() } as Student) : null);
      }, (err) => console.error('own student doc:', err));
      // A student's transfer_requests read rule only permits their OWN request(s) — never
      // a school-wide query here, which would deny the whole `list` the moment the
      // school has any other student's request (see teacherCoversStudent's comment
      // elsewhere in this file for the same "list fails whole" reasoning).
      const unsubOwnTransfer = onSnapshot(
        query(collection(db, 'transfer_requests'), where('studentId', '==', currentUser.id)),
        (snap) => setTransferRequests(snap.docs.map(d => ({ id: d.id, ...d.data() } as TransferRequest))),
        (err) => console.error('transfer_requests (student):', err)
      );
      return () => { unsubInvoices(); unsubClasses(); unsubSections(); unsubTeachers(); unsubOwnStudent(); unsubOwnTransfer(); };
    }

    // For teachers: firestore.rules only allows a teacher to read students in their own
    // assigned classes/sections (see teacherCoversStudent() there) — a school-wide
    // students query would now be denied outright. An array-contains-any query on
    // enrolledClassIds/enrolledSectionIds can't work either — Firestore represents a
    // query-filtered field as an abstract "constraint" during rule evaluation, and a
    // rule can't hasAny() against that (confirmed via emulator). Instead: derive the
    // teacher's own classes from the already-unrestricted classes subscription, read
    // each one's server-maintained studentIds roster (SchoolClass.studentIds, kept in
    // sync by functions/src/index.ts's syncStudentEnrollmentIndex), and fetch students
    // by document ID — chunked at 30 per query, Firestore's `in` operator limit.
    if (currentUser.role === 'teacher') {
      const teacherData = currentUser.data as TeacherAccount | undefined;
      const sectionIds = new Set((teacherData?.sectionIds || []).filter(Boolean));
      const subjectClassIds = new Set((teacherData?.subjects || []).map(s => s.classId).filter(Boolean));

      let studentChunkUnsubs: Array<() => void> = [];
      const clearStudentChunkUnsubs = () => { studentChunkUnsubs.forEach(u => u()); studentChunkUnsubs = []; };

      const refetchStudentsFromRoster = (classList: SchoolClass[]) => {
        clearStudentChunkUnsubs();
        const teacherUname = teacherData?.username || teacherData?.id || currentUser.id;
        const teacherClasses = classList.filter(c =>
          sectionIds.has(c.sectionId) ||
          subjectClassIds.has(c.id) ||
          c.teacherId === teacherUname ||
          c.teacherId === teacherData?.id
        );
        const studentIds = Array.from(new Set(teacherClasses.flatMap(c => c.studentIds || [])));
        if (studentIds.length === 0) { setStudents([]); return; }

        const chunks: string[][] = [];
        for (let i = 0; i < studentIds.length; i += 30) chunks.push(studentIds.slice(i, i + 30));

        const byChunk = new Map<number, Student[]>();
        const pushMergedStudents = () => setStudents(chunks.flatMap((_, idx) => byChunk.get(idx) || []));

        chunks.forEach((chunk, idx) => {
          const qChunk = query(collection(db, 'students'), where(documentId(), 'in', chunk));
          studentChunkUnsubs.push(onSnapshot(qChunk, (snap) => {
            byChunk.set(idx, snap.docs.map(d => ({ id: d.id, ...d.data() } as Student)));
            pushMergedStudents();
          }, async (err) => {
            // A documentId()-in query is denied in full if even ONE candidate in the chunk
            // fails the security rule (e.g. a roster entry whose enrolledClassIds hasn't
            // finished syncing yet after a reassignment). Rather than losing the whole
            // chunk and surfacing a scary "Access Denied" toast, fall back to reading each
            // student individually so the ones the teacher IS allowed to see still show up;
            // only the genuinely-denied ones are silently dropped.
            if (err.code === 'permission-denied' && chunk.length > 1) {
              const results = await Promise.all(chunk.map(async (sid) => {
                try {
                  const d = await getDoc(doc(db, 'students', sid));
                  return d.exists() ? ({ id: d.id, ...d.data() } as Student) : null;
                } catch { return null; }
              }));
              byChunk.set(idx, results.filter((s): s is Student => !!s));
              pushMergedStudents();
            } else {
              handleError('students', err);
            }
          }));
        });
      };

      const unsubClasses = onSnapshot(query(collection(db, 'classes'), where('schoolId', '==', currentSchool.id)), (snap) => {
        const classList = snap.docs.map(d => ({ id: d.id, ...d.data() } as SchoolClass));
        setClasses(classList);
        refetchStudentsFromRoster(classList);
      }, (err) => console.error(err));

      const unsubTeachers = subscribeSchoolScoped<TeacherAccount>(currentSchool.id, 'teachers', setTeachers, (err) => handleError('teachers', err));
      const unsubReviews = subscribeSchoolScoped<Review>(currentSchool.id, 'reviews', setReviews, (err) => console.error(err));
      const unsubInvoices = subscribeSchoolScoped<Invoice>(currentSchool.id, 'invoices', setInvoices, (err) => console.error(err));
      const unsubIssues = subscribeSchoolScoped<IssueReport>(currentSchool.id, 'issue_reports', setIssueReports, (err) => console.error(err));
      const unsubSections = subscribeSchoolScoped<Section>(currentSchool.id, 'sections', handleSectionsLoaded, (err) => console.error(err));

      return () => {
        clearStudentChunkUnsubs();
        unsubClasses();
        unsubTeachers(); unsubReviews(); unsubInvoices(); unsubIssues(); unsubSections();
      };
    }

    const unsubStudents = subscribeSchoolScoped<Student>(currentSchool.id, 'students', setStudents, (err) => handleError('students', err));
    const unsubTeachers = subscribeSchoolScoped<TeacherAccount>(currentSchool.id, 'teachers', setTeachers, (err) => handleError('teachers', err));
    const unsubReviews = subscribeSchoolScoped<Review>(currentSchool.id, 'reviews', setReviews, (err) => console.error(err));
    const unsubInvoices = subscribeSchoolScoped<Invoice>(currentSchool.id, 'invoices', setInvoices, (err) => console.error(err));
    const unsubIssues = subscribeSchoolScoped<IssueReport>(currentSchool.id, 'issue_reports', setIssueReports, (err) => console.error(err));
    const unsubClasses = subscribeSchoolScoped<SchoolClass>(currentSchool.id, 'classes', setClasses, (err) => console.error(err));
    const unsubSections = subscribeSchoolScoped<Section>(currentSchool.id, 'sections', handleSectionsLoaded, (err) => console.error(err));

    // transfer_requests is admin-only in firestore.rules (unlike reviews/issue_reports
    // above, which any same-school role can read) — a non-academic staff session hitting
    // this same-school-broad subscription would get denied outright the moment any
    // request exists, so it's gated to admin/super_admin only, not the shared catch-all.
    const unsubTransfers = (currentUser.role === 'admin' || currentUser.role === 'super_admin')
      ? subscribeSchoolScoped<TransferRequest>(currentSchool.id, 'transfer_requests', setTransferRequests, (err) => console.error(err))
      : () => {};
    // archived_students is admin-only too — same reasoning as transfer_requests above.
    const unsubArchivedStudents = (currentUser.role === 'admin' || currentUser.role === 'super_admin')
      ? subscribeSchoolScoped<ArchivedStudent>(currentSchool.id, 'archived_students', setArchivedStudents, (err) => console.error(err))
      : () => {};

    return () => {
      unsubStudents(); unsubTeachers(); unsubReviews(); unsubInvoices(); unsubIssues(); unsubClasses(); unsubSections(); unsubTransfers(); unsubArchivedStudents();
    };
  }, [currentSchool, currentUser, schoolLoading]);


  // STAGE 2: CORE MANAGEMENT DATA (Payments, Staff, Attendance)
  useEffect(() => {
    if (loadStage < 1 || !currentSchool) return;
    console.log("Starting Stage 2: Core Data Load");

    const q = (col: string) => query(collection(db, col), where('schoolId', '==', currentSchool.id));

    // Stagger this slightly if needed, but useEffect runs after render so it is already staggered from Stage 1
    const timer = setTimeout(() => {
      // Payments & Finance
      const unsubPayments = subscribeSchoolScoped<Payment>(currentSchool.id, 'payments', setPayments, (e) => console.error("Load Error (Stage 2):", e));
      const unsubPayrolls = subscribeSchoolScoped<Payroll>(currentSchool.id, 'payrolls', setPayrolls, (e) => console.error("Load Error (Stage 2):", e));
      const unsubNonAcademic = subscribeSchoolScoped<NonAcademicStaffAccount>(currentSchool.id, 'non_academic_staff', setNonAcademicStaff, (e) => console.error("Load Error (Stage 2):", e));
      const unsubAttendance = subscribeSchoolScoped<AttendanceSubmission>(currentSchool.id, 'attendance_submissions', setAttendanceSubmissions, (e) => console.error("Load Error (Stage 2):", e));
      const unsubIQA = subscribeSchoolScoped<IQASubmission>(currentSchool.id, 'iqa_submissions', setIqaSubmissions, (e) => console.error("Load Error (Stage 2):", e));
      const unsubAnnouncements = subscribeSchoolScoped<Announcement>(currentSchool.id, 'announcements', setAnnouncements, (e) => console.error("Load Error (Stage 2):", e));

      // Trigger next stage
      setLoadStage(2);

      // Cleanup function inside the effect for these listeners? 
      // React Effects must return cleanup. We can't return cleanup from setTimeout.
      // Best practice: Store unsub functions in ref or utilize a separate component?
      // Since we are monolithic here, let's keep it simple.
      // We can use a ref to store unsubs for cleanup on unmount
    }, 100); // short stagger so Stage 1 subscriptions register first

    return () => clearTimeout(timer);
  }, [loadStage, currentSchool]); // Dependency on loadStage triggers 


  // STAGE 3: BACKGROUND DATA (Logs, Archives, Less used)
  useEffect(() => {
    if (loadStage < 2 || !currentSchool) return;
    console.log("Starting Stage 3: Background Data Load");
    const q = (col: string) => query(collection(db, col), where('schoolId', '==', currentSchool.id));

    const timer = setTimeout(() => {
      const unsubResults = onSnapshot(q('published_results'), (snap) => setPublishedResults(snap.docs.map(d => ({ id: d.id, ...d.data() } as PublishedResult)))); // Heavy?
      const unsubFeedback = onSnapshot(q('student_feedback'), (snap) => setStudentFeedback(snap.docs.map(d => ({ id: d.id, ...d.data() } as StudentFeedback))));
      const unsubClockIn = onSnapshot(q('clock_in_records'), (snap) => setClockInRecords(snap.docs.map(d => ({ id: d.id, ...d.data() } as ClockInRecord))));
      const unsubSalaryAdvances = onSnapshot(q('salary_advances'), (snap) => setSalaryAdvances(snap.docs.map(d => ({ id: d.id, ...d.data() } as SalaryAdvanceRequest))));
      const unsubExpenseClaims = onSnapshot(q('expense_claims'), (snap) => setExpenseClaims(snap.docs.map(d => ({ id: d.id, ...d.data() } as ExpenseClaim))));
      const unsubObservations = onSnapshot(q('observations'), (snap) => setObservations(snap.docs.map(d => ({ id: d.id, ...d.data() } as Observation))));
      const unsubActionPlans = onSnapshot(q('action_plan_items'), (snap) => setActionPlanItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as ActionPlanItem))));
      const unsubStandardisation = onSnapshot(q('standardisation_files'), (snap) => setStandardisationFiles(snap.docs.map(d => ({ id: d.id, ...d.data() } as StandardisationFile))));
      const unsubCBT = onSnapshot(q('cbt_tests'), (snap) => setCbtTests(snap.docs.map(d => ({ id: d.id, ...d.data() } as CBT_Test))));
      const unsubMeetings = onSnapshot(q('standardization_meetings'), (snap) => setStandardizationMeetings(snap.docs.map(d => ({ id: d.id, ...d.data() } as StandardizationMeeting))));
      const unsubArchives = onSnapshot(q('archives'), (snap) => setArchives(snap.docs.map(d => ({ id: d.id, ...d.data() } as Archive))));
      const unsubAdmins = onSnapshot(q('admins'), (snap) => setAdmins(snap.docs.map(d => ({ id: d.id, ...d.data() } as AdminAccount))));
      const unsubNotifications = onSnapshot(q('notifications'), (snap) => setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() } as Notification))));
      onSnapshot(q('admission_applications'), (snap) => setAdmissionApplications(snap.docs.map(d => ({ id: d.id, ...d.data() } as AdmissionApplication))));
      onSnapshot(q('teacherApplications'), (snap) => setTeacherApplications(snap.docs.map(d => ({ id: d.id, ...d.data() } as TeacherApplication))));
      // MOVED FEES TO DEDICATED EFFECT
      const unsubIQASampling = onSnapshot(q('iqa_sampling_plan'), (snap) => setIqaSamplingPlan(snap.docs.map(d => ({ id: d.id, ...d.data() } as IQASamplingPlanEntry))));

      setLoadStage(3);
    }, 200); // short stagger so Stage 2 subscriptions register first

    return () => clearTimeout(timer);
  }, [loadStage, currentSchool]);

  // DEDICATED FINANCE LISTENERS (Reliable Sync)
  useEffect(() => {
    if (!currentSchool) return;
    // Start these earlier (Stage 1 or 2 equivalent) or just immediately when school matches
    const q = (col: string) => query(collection(db, col), where('schoolId', '==', currentSchool.id));

    const unsubFeeCategories = onSnapshot(q('fee_categories'), (snap) => setFeeCategories(snap.docs.map(d => ({ id: d.id, ...d.data() } as FeeCategory))));
    const unsubFeeTemplates = onSnapshot(q('fee_templates'), (snap) => setFeeTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() } as FeeTemplate))));
    const unsubDiscounts = onSnapshot(q('discounts'), (snap) => setDiscounts(snap.docs.map(d => ({ id: d.id, ...d.data() } as Discount))));
    const unsubSummerPrograms = onSnapshot(q('summer_programs'), (snap) => setSummerPrograms(snap.docs.map(d => ({ id: d.id, ...d.data() } as SummerProgram))));
    const unsubSummerEnrollments = onSnapshot(q('summer_enrollments'), (snap) => setSummerEnrollments(snap.docs.map(d => ({ id: d.id, ...d.data() } as SummerEnrollment))));
    const unsubSummerProgramSignups = onSnapshot(q('summer_program_signups'), (snap) => setSummerProgramSignups(snap.docs.map(d => ({ id: d.id, ...d.data() } as SummerProgramSignup))));

    return () => {
      unsubFeeCategories();
      unsubFeeTemplates();
      unsubDiscounts();
      unsubSummerPrograms();
      unsubSummerEnrollments();
      unsubSummerProgramSignups();
    };
  }, [currentSchool]);



  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    document.title = schoolSettings.schoolName || brand.appName;
  }, [schoolSettings]);

  // --- SECTION HANDLERS ---
  const handleAddSection = useCallback(async (section: Omit<Section, 'id'>) => {
    if (sections.some(s => s.name.toLowerCase() === section.name.toLowerCase())) {
      setToast({ message: 'A section with this name already exists.', type: 'error' });
      return;
    }
    try {
      if (!currentSchool) throw new Error("No school context");
      await addDoc(collection(db, 'sections'), { ...section, schoolId: currentSchool.id });
      setToast({ message: 'Section created successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error creating section', type: 'error' }); }
  }, [sections, currentSchool]);

  const handleUpdateSection = useCallback(async (section: Section) => {
    try {
      await setDoc(doc(db, 'sections', section.id), section);
      setToast({ message: 'Section updated successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating section', type: 'error' }); }
  }, []);

  const handleDeleteSection = useCallback(async (sectionId: string) => {
    const isUsedByClass = classes.some(c => c.sectionId === sectionId);
    const isUsedByTeacher = teachers.some(t => t.sectionIds.includes(sectionId));

    if (isUsedByClass || isUsedByTeacher) {
      setToast({ message: 'Cannot delete section. It is currently assigned to classes or teachers.', type: 'error' });
      return;
    }
    try {
      await deleteDoc(doc(db, 'sections', sectionId));
      setToast({ message: 'Section deleted successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting section', type: 'error' }); }
  }, [classes, teachers]);

  // --- STUDENT HANDLERS ---
  const handleAddStudent = useCallback(async (student: Omit<Student, 'id'>) => {
    try {
      if (!currentSchool) throw new Error("No school context");
      const cleanStudent = cleanData({
        ...student,
        email: student.email ? student.email.toLowerCase().trim() : student.email,
        schoolId: currentSchool.id
      });
      await addDoc(collection(db, 'students'), cleanStudent);
      setToast({ message: 'Student enrolled successfully.', type: 'success' });
    } catch (e: any) { console.error(e); setToast({ message: `Error enrolling student: ${e.message || 'Unknown error'}`, type: 'error' }); }
  }, [currentSchool]);

  const handleUpdateStudent = useCallback(async (student: Student) => {
    try {
      const cleanStudent = {
        ...student,
        email: student.email ? student.email.toLowerCase().trim() : student.email
      };
      await setDoc(doc(db, 'students', student.id), cleanStudent);
      setToast({ message: 'Student data saved.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating student', type: 'error' }); }
  }, []);

  const handleDeleteStudent = useCallback(async (studentId: string) => {
    const studentToDelete = students?.find(s => s.id === studentId);
    if (!studentToDelete) {
      setToast({ message: 'Could not find student to delete.', type: 'error' });
      return;
    }

    try {
      await deleteDoc(doc(db, 'students', studentId));
      // Note: Cascading deletes (invoices, payments, etc.) are harder in client-side Firestore.
      // For now, we will just delete the student record. In a real app, use Cloud Functions.
      setToast({ message: 'Student record deleted.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting student', type: 'error' }); }
  }, [students]);

  // --- CLASS HANDLERS ---
  const handleAddClass = useCallback(async (schoolClass: Omit<SchoolClass, 'id'>) => {
    try {
      if (!currentSchool) throw new Error("No school context");
      await addDoc(collection(db, 'classes'), { ...schoolClass, schoolId: currentSchool.id });
      setToast({ message: 'Class created successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error creating class', type: 'error' }); }
  }, [currentSchool]);

  const handleUpdateClass = useCallback(async (schoolClass: SchoolClass) => {
    try {
      const sanitizedClass = cleanData(schoolClass);
      await setDoc(doc(db, 'classes', schoolClass.id), sanitizedClass);
    } catch (e) { console.error(e); setToast({ message: 'Error updating class', type: 'error' }); }
  }, []);

  const handleDeleteClass = useCallback(async (classId: string) => {
    if (students.some(s => s.enrollments.some(e => e.classId === classId))) {
      setToast({ message: 'Cannot delete class with students enrolled. Please transfer students first.', type: 'error' });
      return;
    }
    try {
      await deleteDoc(doc(db, 'classes', classId));
      setToast({ message: 'Class deleted successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting class', type: 'error' }); }
  }, [students]);

  // --- TEACHER HANDLERS ---
  const handleAddTeacher = useCallback(async (teacher: Omit<TeacherAccount, 'role' | 'active'>) => {
    if (teachers.some(t => t.username === teacher.username)) {
      setToast({ message: `Teacher with username "${teacher.username}" already exists.`, type: 'error' });
      return;
    }
    try {
      if (!currentSchool) throw new Error("No school context");
      await setDoc(doc(db, 'teachers', teacher.username), { ...teacher, role: 'teacher', active: true, schoolId: currentSchool.id }); // Use username as ID

      // This doc write alone doesn't create a Firebase Auth account — without one, the
      // teacher can never actually log in. Create it now using the same self-healing
      // function the Edit Teacher flow uses.
      if (teacher.email && (teacher as any).password) {
        try {
          const updateAuthCreds = httpsCallable(getFunctions(), 'updateUserAuthCredentials');
          const result: any = await updateAuthCreds({
            email: teacher.email,
            newEmail: teacher.email,
            newPassword: (teacher as any).password,
          });
          const returnedUid = result?.data?.uid as string | undefined;
          if (returnedUid) {
            await updateDoc(doc(db, 'teachers', teacher.username), { authUid: returnedUid });
            // teachers/{username} isn't reliably guessable by uid/email/emailPrefix in
            // firestore.rules — this direct pointer is what lets getRole()/getSchoolId()
            // resolve this teacher without guessing. See the uid_index comment there.
            await setDoc(doc(db, 'uid_index', returnedUid), {
              role: 'teacher', schoolId: currentSchool.id, docId: teacher.username, collectionName: 'teachers',
              // Mirrored so firestore.rules' teacherCoversStudent() never needs a second
              // get() into teachers/ (that pattern throws during `list` rule evaluation).
              // functions/src/index.ts's syncTeacherSubjectIndex trigger keeps this in
              // sync on every subsequent teacher doc write.
              sectionIds: (teacher as any).sectionIds || [],
              subjectClassIds: ((teacher as any).subjects || []).map((s: any) => s.classId).filter(Boolean),
            });
          }
        } catch (err: any) {
          console.error('Failed to create Firebase Auth account for teacher:', err);
          setToast({ message: `Teacher record saved, but login account creation failed: ${err.message || err}`, type: 'error' });
          return;
        }
      }

      setToast({ message: `Teacher ${teacher.fullName} created successfully.`, type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error creating teacher', type: 'error' }); }
  }, [teachers, currentSchool]);

  const handleApproveApplication = useCallback(async (application: TeacherApplication, opts?: { assignedClassId?: string; assignedClassName?: string; assignedStudentIds?: string[] }): Promise<{ username: string; password: string }> => {
    if (!currentSchool) throw new Error('No school context');
    const username = application.email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
    const { password } = await registerUser('teachers', {
      email: application.email,
      role: 'teacher',
      fullName: application.fullName,
      phone: application.phone,
      qualification: application.qualification,
      subjects: application.subjects ? [{ name: application.subjects }] : [],
      schoolId: currentSchool.id,
      active: true,
      username,
      passwordPrefix: 'MEF',
    });
    await updateDoc(doc(db, 'teacherApplications', application.id!), {
      status: 'approved',
      reviewedAt: serverTimestamp(),
      reviewedBy: currentUser?.displayName || currentUser?.email || 'Admin',
      createdUsername: username,
      ...(opts?.assignedClassId ? { assignedClassId: opts.assignedClassId, assignedClassName: opts.assignedClassName || '' } : {}),
      ...(opts?.assignedStudentIds?.length ? { assignedStudentIds: opts.assignedStudentIds } : {}),
    });
    setToast({ message: `${application.fullName} approved and account created.`, type: 'success' });
    return { username, password };
  }, [currentSchool, currentUser]);

  const handleRejectApplication = useCallback(async (applicationId: string, reason: string) => {
    await updateDoc(doc(db, 'teacherApplications', applicationId), {
      status: 'rejected',
      rejectionReason: reason,
      reviewedAt: serverTimestamp(),
      reviewedBy: currentUser?.displayName || currentUser?.email || 'Admin',
    });
    setToast({ message: 'Application rejected.', type: 'success' });
  }, [currentUser]);

  const handleScheduleInterview = useCallback(async (applicationId: string, details: { date: string; mode: 'in-person' | 'video-call'; notes: string }) => {
    await updateDoc(doc(db, 'teacherApplications', applicationId), {
      status: 'interview_scheduled',
      interviewDate: details.date,
      interviewMode: details.mode,
      interviewNotes: details.notes,
      reviewedAt: serverTimestamp(),
      reviewedBy: currentUser?.displayName || currentUser?.email || 'Admin',
    });
    setToast({ message: 'Interview scheduled and applicant notified.', type: 'success' });
  }, [currentUser]);

  const handleDeleteApplications = useCallback(async (applicationIds: string[]) => {
    try {
      const batch = writeBatch(db);
      applicationIds.forEach(id => {
        batch.delete(doc(db, 'teacherApplications', id));
      });
      await batch.commit();
      setToast({ message: `${applicationIds.length} application(s) deleted permanently.`, type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Failed to delete applications: ${e.message}`, type: 'error' });
      throw e;
    }
  }, []);

  const handleClearAllAttendance = useCallback(async () => {
    try {
      const qRef = collection(db, 'clock_in_records');
      const snap = await getDocs(qRef);
      if (snap.empty) {
        setToast({ message: 'No attendance records found to clear.', type: 'info' });
        return;
      }
      const docs = snap.docs;
      const chunks = [];
      for (let i = 0; i < docs.length; i += 500) {
        chunks.push(docs.slice(i, i + 500));
      }
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }
      setToast({ message: `Permanently cleared ${docs.length} staff attendance records.`, type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Failed to clear attendance: ${e.message}`, type: 'error' });
      throw e;
    }
  }, []);

  const handleUpdateTeacher = useCallback(async (teacher: TeacherAccount): Promise<string | void> => {
    try {
      const targetId = (teacher as any).id || teacher.username;
      if (!targetId) throw new Error("Teacher ID or username is missing");

      const { password, ...teacherWithoutPassword } = teacher as any;
      await setDoc(doc(db, 'teachers', targetId), cleanData(teacherWithoutPassword), { merge: true });
      // Keep the Firebase Auth account's login email and (if provided) password in sync
      // with Firestore. Resolved by the stored Auth UID when known — which may differ
      // from the Firestore doc ID for teachers added directly rather than via an approved
      // application, since only the latter path created a real Auth account historically.
      // If no Auth account is found under that UID (or under the email), the function
      // creates one and returns its UID, which we then save as `authUid` so every future
      // save targets it directly instead of retrying the lookup.
      const existingAuthUid = (teacher as any).authUid as string | undefined;
      const uid = existingAuthUid || (teacher as any).id as string | undefined;
      if (uid || teacher.email) {
        const updateAuthCreds = httpsCallable(getFunctions(), 'updateUserAuthCredentials');
        try {
          const result: any = await updateAuthCreds({
            uid,
            email: uid ? undefined : teacher.email,
            newEmail: teacher.email || undefined,
            newPassword: password || undefined,
          });
          const returnedUid = result?.data?.uid as string | undefined;
          if (returnedUid && returnedUid !== existingAuthUid) {
            await updateDoc(doc(db, 'teachers', targetId), { authUid: returnedUid });
          }
        } catch (err: any) {
          // The Firestore record is already saved — report the login-sync failure as a
          // warning instead of throwing, so the caller still applies class assignments.
          console.error('Failed to sync Firebase Auth credentials for teacher:', err);
          const warning = `Teacher record saved, but syncing login credentials failed: ${err.message || err}`;
          setToast({ message: warning, type: 'error' });
          return warning;
        }
      }
      setToast({ message: `Teacher ${teacher.fullName} updated successfully.`, type: 'success' });
    } catch (e: any) {
      console.error("Error updating teacher:", e);
      setToast({ message: `Error updating teacher: ${e.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, []);

  // Flips a teacher's active/inactive status only — deliberately Firestore-only, with no
  // Firebase Auth sync attempt, since this has nothing to do with login credentials and
  // shouldn't be able to fail on an Auth-permission check the way a credentials edit can.
  const handleToggleTeacherActive = useCallback(async (teacher: TeacherAccount, active: boolean) => {
    try {
      const targetId = (teacher as any).id || teacher.username;
      await updateDoc(doc(db, 'teachers', targetId), { active });
      setToast({ message: `${teacher.fullName} marked ${active ? 'active' : 'inactive'}.`, type: 'success' });
    } catch (e: any) {
      console.error('Error toggling teacher active status:', e);
      setToast({ message: `Error updating status: ${e.message || 'Unknown error'}`, type: 'error' });
    }
  }, []);

  const handleDeleteTeacher = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'teachers', id));
      // Also update classes to remove teacherId? Cloud Function better.
      setToast({ message: `Teacher record deleted.`, type: 'success' });
    } catch (e) {
      console.error(e);
      setToast({ message: 'Error deleting teacher', type: 'error' });
      throw e;
    }
  }, []);

  // --- REVIEW HANDLERS ---
  const handleAddReview = useCallback(async (review: Omit<Review, 'id' | 'createdAt' | 'status'>, existingReviewId?: string) => {
    try {
      if (!currentSchool) throw new Error("No school context");
      if (existingReviewId) {
        await updateDoc(doc(db, 'reviews', existingReviewId), cleanData({
          ...review,
          status: 'pending',
          adminComment: '', // Clear rejection comment
          submittedAt: Date.now() // Optional: track resubmission time if needed, or just rely on createdAt
        }));
        setToast({ message: 'Grade review resubmitted successfully.', type: 'success' });
      } else {
        await addDoc(collection(db, 'reviews'), cleanData({
          ...review,
          schoolId: currentSchool.id,
          createdAt: Date.now(),
          status: 'pending'
        }));
        setToast({ message: 'Grade review submitted successfully.', type: 'success' });
      }
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error submitting review: ${e.message || 'Unknown error'}`, type: 'error' });
    }
  }, [currentSchool]);

  const handleUpdateReview = useCallback(async (reviewId: string, status: 'approved' | 'rejected', adminComment?: string, updatedStudents?: Student[]) => {
    console.log('handleUpdateReview called:', { reviewId, status });
    const review = reviews.find(r => r.id === reviewId);
    if (!review) {
      console.error('Review not found:', reviewId);
      return;
    }

    try {
      if (status === 'approved') {
        const section = sections?.find(s => s.id === review.sectionId);
        const teacher = teachers?.find(t => t.username === review.teacher);
        const relevantAttendance = attendanceSubmissions.filter(sub => sub.term === review.term && sub.status === 'approved');

        const fullStudentsForReview = (updatedStudents || review.students)
          .map(reviewStudent => {
            const fullStudent = students?.find(s => s.id === reviewStudent.id);
            if (!fullStudent) {
              // The live student record couldn't be matched (e.g. the student was
              // re-imported/edited after the review was submitted and now has a
              // different id). The review already carries a full snapshot of the
              // student at submission time, so fall back to that instead of
              // aborting the whole approval and silently dropping the student.
              console.warn(`Review approval: no live student match for id ${reviewStudent.id} (${reviewStudent.fullName || 'unknown'}). Using submitted snapshot instead.`);
              return reviewStudent;
            }
            // Merge review data (grades, comments) into full student data
            return { ...fullStudent, ...reviewStudent };
          });

        const resitNotifications: { studentId: string; studentName: string; subjectName: string }[] = [];

        const newResults: PublishedResult[] = fullStudentsForReview.map(student => {
          // Calculate attendance for this student
          const counts = { early: 0, onTime: 0, late: 0, absent: 0, total: 0 };
          relevantAttendance.forEach(sub => {
            const studentWeek = sub.attendanceData ? sub.attendanceData[student.id] : undefined;
            if (studentWeek) {
              Object.values(studentWeek).forEach(status => {
                counts.total++;
                if (status === AttendanceStatus.PRESENT_EARLY) counts.early++;
                else if (status === AttendanceStatus.PRESENT_ON_TIME) counts.onTime++;
                else if (status === AttendanceStatus.LATE_MINOR || status === AttendanceStatus.LATE_MAJOR) counts.late++;
                else if (status === AttendanceStatus.ABSENT) counts.absent++;
              });
            }
          });
          const presentDays = counts.early + counts.onTime + counts.late;
          const attendanceSummary = {
            rate: counts.total > 0 ? `${((presentDays / counts.total) * 100).toFixed(0)}%` : 'N/A',
            summary: `${presentDays} out of ${counts.total} days attended.`,
            breakdown: counts
          };

          // Generate collision-proof ID using student ID, term and class name
          const cleanClassName = (review.className || '').trim().replace(/[^a-zA-Z0-9-_]/g, '_');
          const resultId = `${student.id}_${review.term}_${cleanClassName}`;

          return {
            id: resultId,
            surname: student.surname || '',
            nin: student.nin || '',
            fullName: student.fullName || '',
            reg: student.reg || '',
            photoDataUrl: student.photoDataUrl || '',
            sectionName: section?.name || 'Unknown Section',
            className: review.className,
            term: review.term,
            school: review.profile.school,
            session: review.profile.session,
            teacherName: review.profile.name,
            teacherDept: review.profile.dept,
            subjects: (student.subjects || []).filter(s => {
              // 0. Term Check: Ensure we only include subjects for the term being approved
              const sTerm = typeof s.term === 'number' ? s.term : Number(s.term || 1);
              if (sTerm !== Number(review.term)) return false;

              const isIslamiyya = review.className.toLowerCase().includes('islamiyya') || review.className.toLowerCase().includes('tahfiz') || section?.name.toLowerCase().includes('islamiyya');

              // 1. Exact match on classId is authoritative
              if (review.classId && s.classId) return s.classId === review.classId;

              // 2. If subject has classId but review doesn't (or mismatch), exclude
              if (s.classId && review.classId && s.classId !== review.classId) return false;

              // 3. STRICT FILTER FOR ISLAMIYYA: 
              // If it's an Islamiyya review, and subject has NO classId (legacy), 
              // we ensure it is NOT a Conventional subject.
              // We assume Islamiyya subjects have Arabic names OR are explicitly linked.
              if (isIslamiyya && !s.classId) {
                const hasArabic = /[\u0600-\u06FF]/.test(s.name);
                if (!hasArabic) return false; // Exclude English-only subjects from Islamiyya report
              }

              return true;
            }).map(s => {
              const ca1Num = Number(s.ca1) || 0;
              const ca2Num = Number(s.ca2) || 0;
              const examNum = Number(s.exam) || 0;
              const total = ca1Num + ca2Num + examNum;
              let grade = 'F9'; let remark = 'Fail';
              if (total >= 75) { grade = 'A1'; remark = 'Excellent'; }
              else if (total >= 70) { grade = 'B2'; remark = 'Very Good'; }
              else if (total >= 65) { grade = 'B3'; remark = 'Good'; }
              else if (total >= 60) { grade = 'C4'; remark = 'Credit'; }
              else if (total >= 55) { grade = 'C5'; remark = 'Credit'; }
              else if (total >= 50) { grade = 'C6'; remark = 'Credit'; }
              else if (total >= 40) { grade = 'D7'; remark = 'Pass'; } // Added D7
              else if (total >= 45) { grade = 'E8'; remark = 'Pass'; } // Added E8 - Correction KCA standard? 
              // Wait, KCA Standard: 
              // 75-100: A1
              // 70-74: B2
              // 65-69: B3
              // 60-64: C4
              // 55-59: C5
              // 50-54: C6
              // 45-49: D7 ?
              // 40-44: E8 ?
              // 0-39: F9
              // I will use standard WAEC/NECO if not specified, but usually it's:
              // A1: 75+, B2: 70+, B3: 65+, C4: 60+, C5: 55+, C6: 50+, D7: 45+, E8: 40+, F9: <40.

              // Refined Grading Logic:
              if (total >= 75) { grade = 'A1'; remark = 'Excellent'; }
              else if (total >= 70) { grade = 'B2'; remark = 'Very Good'; }
              else if (total >= 65) { grade = 'B3'; remark = 'Good'; }
              else if (total >= 60) { grade = 'C4'; remark = 'Credit'; }
              else if (total >= 55) { grade = 'C5'; remark = 'Credit'; }
              else if (total >= 50) { grade = 'C6'; remark = 'Credit'; }
              else if (total >= 45) { grade = 'D7'; remark = 'Pass'; }
              else if (total >= 40) { grade = 'E8'; remark = 'Pass'; }
              else { grade = 'F9'; remark = 'Fail'; }

              // Translate remark to Arabic if Islamiyya
              if (review.className.toLowerCase().includes('islamiyya') || review.className.toLowerCase().includes('tahfiz') || section?.name.toLowerCase().includes('islamiyya')) {
                const arabicRemarks: Record<string, string> = {
                  'Excellent': 'ممتاز',
                  'Very Good': 'جيد جداً',
                  'Good': 'جيد',
                  'Credit': 'جيد',
                  'Pass': 'مقبول',
                  'Fail': 'راسب'
                };
                remark = arabicRemarks[remark] || remark;
              }

              if (grade === 'F9' && isResitSubject(s.name) && s.resitStatus !== 'passed') {
                resitNotifications.push({ studentId: student.id, studentName: student.fullName || 'Student', subjectName: s.name });
              }

              // Resit outcome is decided from the FINAL admin-approved grade, not whatever
              // the teacher's edit implied when they submitted — mirrors handleRecordResit's
              // own passed/failed logic, just gated behind approval instead of writing the
              // instant a score is typed. Only touches subjects genuinely in a resit flow
              // (scheduled/failed already, or just resit-edited in the Gradebook, signalled
              // by originalExamBeforeResit now being set) — zero effect on regular grades.
              const wasInResitFlow = s.resitStatus === 'scheduled' || s.resitStatus === 'failed' || s.originalExamBeforeResit !== undefined;
              const resitFields = (isResitSubject(s.name) && wasInResitFlow)
                ? {
                    resitStatus: (grade === 'F9' ? 'failed' : 'passed') as 'passed' | 'failed',
                    resitScore: examNum,
                    resitRecordedAt: Date.now(),
                    // Recorded-by is whoever typed the score into the Gradebook (the
                    // submitting teacher) — approved-by is this admin, right now.
                    resitRecordedBy: review.teacher,
                    resitRecordedByName: review.profile?.name || review.teacher,
                    resitApprovedBy: currentUser?.id || 'unknown',
                    resitApprovedByName: getActingUserName(),
                    resitApprovedAt: Date.now(),
                  }
                : {};

              return {
                ...s,
                ca1: ca1Num,
                ca2: ca2Num,
                exam: examNum,
                total,
                grade,
                remark,
                ...resitFields,
              };
            }),
            teacherComment: student.teacherComment,
            headComment: student.headComment, // Now includes HOS comment from admin
            extracurricularActivities: student.extracurricularActivities,
            adminComment: adminComment || '',
            adminSignature: '',
            approvedAt: Date.now(),
            archived: false,
            teacher: review.teacher,
            teacherSignatureUrl: teacher?.signatureDataUrl || '',
            headOfSchoolSignatureUrl: schoolSettings.headOfSchoolSignatureUrl || '',
            schoolStampUrl: schoolSettings.schoolStampUrl || '',
            attendanceSummary: attendanceSummary,
            studentId: student.id, // Added for filtering in StudentPortal
            classId: review.classId, // Critical for deep linking
            sectionId: review.sectionId, // Critical for deep linking
            schoolId: currentSchool?.id || 'kca-main', // Ensure visibility
          };
        });

        // Batch write results
        const batch = writeBatch(db);
        console.log(`Generating ${newResults.length} published results.`);
        newResults.forEach((res) => {
          console.log('Adding result to batch:', res.id);
          batch.set(doc(db, 'published_results', res.id), cleanData(res));
        });

        // Add to Archives
        const archiveRef = doc(collection(db, 'archives'));
        console.log('Adding to archives:', archiveRef.id);
        batch.set(archiveRef, cleanData({
          ...review,
          id: archiveRef.id,
          status: 'approved',
          logo: schoolSettings.logoDataUrl || '',
          adminSignature: '', // Add logic for admin signature if needed
          schoolId: currentSchool?.id || 'kca-main',
          approvedAt: Date.now()
        }));

        // Optional: Update students collection with HOS comments for persistence in gradebook
        if (updatedStudents) {
          updatedStudents.forEach((s) => {
            const exists = students?.some(liveStudent => liveStudent.id === s.id);
            if (exists) {
              batch.update(doc(db, 'students', s.id), { headComment: s.headComment || '' });
            }
          });
        }

        // Add individual notifications for students in the batch
        fullStudentsForReview.forEach(s => {
          const sNotifRef = doc(collection(db, 'notifications'));
          batch.set(sNotifRef, {
            id: sNotifRef.id,
            title: 'Results Published',
            message: `Your results for ${review.className} (${review.term} Term) are now available.`,
            type: 'academic',
            recipientId: s.id,
            createdAt: Date.now(),
            readBy: []
          });
        });

        // Notify students (and their class teacher) the moment a resit becomes required —
        // don't wait for someone to open Academics and spot the badge.
        const notifiedTeacherIds = new Set<string>();
        resitNotifications.forEach(({ studentId, studentName, subjectName }) => {
          const studentNotifRef = doc(collection(db, 'notifications'));
          batch.set(studentNotifRef, {
            id: studentNotifRef.id,
            title: 'Resit Required',
            message: `You have an F9 in ${subjectName} and need to resit it. Check your Academics tab.`,
            type: 'academic',
            recipientId: studentId,
            createdAt: Date.now(),
            readBy: []
          });
          const student = fullStudentsForReview.find(s => s.id === studentId);
          const classTeacherId = student ? classes?.find(c => student.enrollments?.some(e => e.classId === c.id))?.teacherId : undefined;
          if (classTeacherId && !notifiedTeacherIds.has(`${classTeacherId}_${studentId}_${subjectName}`)) {
            notifiedTeacherIds.add(`${classTeacherId}_${studentId}_${subjectName}`);
            const teacherNotifRef = doc(collection(db, 'notifications'));
            batch.set(teacherNotifRef, {
              id: teacherNotifRef.id,
              title: 'Student Resit Required',
              message: `${studentName} has an F9 in ${subjectName} and needs a resit scheduled.`,
              type: 'academic',
              recipientId: classTeacherId,
              createdAt: Date.now(),
              readBy: []
            });
          }
          const adminNotifRef = doc(collection(db, 'notifications'));
          batch.set(adminNotifRef, {
            id: adminNotifRef.id,
            title: 'Student Resit Required',
            message: `${studentName} has an F9 in ${subjectName} and needs a resit scheduled.`,
            type: 'academic',
            recipientId: 'admin_broadcast',
            createdAt: Date.now(),
            readBy: []
          });
        });

        // Find and clean up any duplicate pending reviews for the same class and term
        const duplicateReviews = reviews.filter(r => 
          r.id !== reviewId && 
          r.className === review.className && 
          r.term === review.term && 
          r.status === 'pending'
        );
        duplicateReviews.forEach(dup => {
          batch.update(doc(db, 'reviews', dup.id), cleanData({ status, adminComment, students: [] }));
        });

        // Update reviews status and clear students to save space
        batch.update(doc(db, 'reviews', reviewId), cleanData({ status, adminComment, students: [] }));

        console.log('Committing single combined batch...');
        await batch.commit();
        console.log('Combined batch committed successfully.');

        setToast({ message: 'Review approved, results published, and archived.', type: 'success' });
      } else {
        // Status is 'rejected', clean up this review and all duplicate pending reviews
        const batch = writeBatch(db);
        batch.update(doc(db, 'reviews', reviewId), cleanData({ status, adminComment, students: [] }));
        const duplicateReviews = reviews.filter(r => 
          r.id !== reviewId && 
          r.className === review.className && 
          r.term === review.term && 
          r.status === 'pending'
        );
        duplicateReviews.forEach(dup => {
          batch.update(doc(db, 'reviews', dup.id), cleanData({ status, adminComment, students: [] }));
        });
        await batch.commit();
        setToast({ message: `Review has been ${status}.`, type: 'success' });
      }

    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error updating review: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e; // Let the caller (AdminPortal) know it failed, so it doesn't reset the review view as if it succeeded.
    }
  }, [reviews, sections, teachers, schoolSettings, attendanceSubmissions, students, currentUser, getActingUserName]);

  const handleSaveReviewDraft = useCallback(async (reviewId: string, updatedStudents: Student[]) => {
    try {
      await updateDoc(doc(db, 'reviews', reviewId), cleanData({
        students: updatedStudents
      }));
      setToast({ message: 'Draft comments saved successfully.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error saving draft: ${e.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, []);

  const handleDeleteReview = useCallback(async (reviewId: string) => {
    if (!window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'reviews', reviewId));
      setToast({ message: 'Review deleted successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting review', type: 'error' }); }
  }, []);

  // Removes one student from an already-archived class result (e.g. they left the school
  // after the term's grades were archived) — also clears their matching published_results
  // doc so a departed student's report card doesn't linger anywhere.
  const handleRemoveStudentFromArchive = useCallback(async (archiveId: string, studentId: string) => {
    try {
      const archive = archives.find(a => a.id === archiveId);
      if (!archive) throw new Error('Archive not found');
      const updatedStudents = archive.students.filter(s => s.id !== studentId);
      await updateDoc(doc(db, 'archives', archiveId), cleanData({ students: updatedStudents }));

      const matching = publishedResults.filter(r =>
        r.studentId === studentId &&
        r.className === archive.className &&
        Number(r.term) === Number(archive.term) &&
        r.session === archive.profile.session
      );
      await Promise.all(matching.map(r => deleteDoc(doc(db, 'published_results', r.id))));

      setToast({ message: 'Student removed from archive.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error removing student from archive: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, [archives, publishedResults]);

  // --- IQA HANDLERS ---
  const handleAddIQASubmission = useCallback(async (submission: Omit<IQASubmission, 'id' | 'submittedAt' | 'status'>) => {
    try {
      if (!currentSchool) throw new Error("No school context");
      await addDoc(collection(db, 'iqa_submissions'), {
        ...submission,
        schoolId: currentSchool.id,
        submittedAt: Date.now(),
        status: 'pending'
      });
      setToast({ message: `${submission.formType} submitted successfully.`, type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error submitting IQA', type: 'error' }); }
  }, [currentSchool]);

  const handleUpdateIQASubmission = useCallback(async (submissionId: string, status: 'approved' | 'rejected', adminComment?: string) => {
    try {
      await updateDoc(doc(db, 'iqa_submissions', submissionId), { status, adminComment });
      setToast({ message: `IQA Submission has been ${status}.`, type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating IQA', type: 'error' }); }
  }, []);

  // --- ATTENDANCE HANDLERS ---
  const handleAddAttendanceSubmission = useCallback(async (submission: Omit<AttendanceSubmission, 'id' | 'submittedAt' | 'status'>) => {
    try {
      if (!currentSchool) throw new Error("No school context");
      await addDoc(collection(db, 'attendance_submissions'), {
        ...submission,
        schoolId: currentSchool.id,
        submittedAt: Date.now(),
        status: 'pending'
      });
      setToast({ message: `Weekly attendance submitted for admin review.`, type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error submitting attendance', type: 'error' }); }
  }, [currentSchool]);

  const handleUpdateAttendanceSubmission = useCallback(async (submissionId: string, status: 'approved' | 'rejected', adminComment?: string) => {
    try {
      const updates: any = { status };
      if (adminComment) updates.adminComment = adminComment;
      await updateDoc(doc(db, 'attendance_submissions', submissionId), updates);
      setToast({ message: `Attendance submission has been ${status}.`, type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating attendance', type: 'error' }); }
  }, []);

  // --- ANNOUNCEMENT HANDLERS ---
  const handleAddAnnouncement = useCallback(async (announcement: Omit<Announcement, 'id' | 'createdAt'>) => {
    try {
      if (!currentSchool) throw new Error("No school context");
      await addDoc(collection(db, 'announcements'), cleanData({
        ...announcement,
        schoolId: currentSchool.id,
        createdAt: Date.now()
      }));
      setToast({ message: 'Announcement published successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error publishing announcement', type: 'error' }); }
  }, [currentSchool]);

  const handleDeleteAnnouncement = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'announcements', id));
      setToast({ message: 'Announcement deleted.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting announcement', type: 'error' }); }
  }, []);

  // --- FINANCE HANDLERS ---
  const handleAddExpenseClaim = useCallback(async (claim: Omit<ExpenseClaim, 'id'>) => {
    try {
      if (!currentSchool) throw new Error("No school context");
      await addDoc(collection(db, 'expense_claims'), { ...claim, schoolId: currentSchool.id });
      setToast({ message: 'Expense claim submitted successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error submitting claim', type: 'error' }); }
  }, [currentSchool]);

  const handleUpdateExpenseClaim = useCallback(async (claimId: string, status: ExpenseClaim['status'], adminComment?: string) => {
    try {
      await updateDoc(doc(db, 'expense_claims', claimId), { status, adminComment });
      setToast({ message: `Expense claim has been ${status}.`, type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating claim', type: 'error' }); }
  }, []);

  /** who is doing a money action, and the school's receipt prefix (for the finance log and receipts) */
  const financeActor = useMemo(() => ({ id: currentUser?.id || 'unknown', name: (currentUser?.data?.fullName || currentUser?.data?.name || currentUser?.email || 'Staff') as string }), [currentUser]);
  const financeSchool = useMemo(() => ({ id: currentSchool?.id || 'kca-main', prefix: schoolSettings.schoolAbbreviation?.replace(/[^A-Za-z]/g, '').toUpperCase() || schoolInitials(schoolSettings.schoolName) }), [currentSchool, schoolSettings]);

  /**
   * Bill students for a term (one invoice each, skipping anyone already billed for that term and
   * session). A student re-enrolling asks the server, which builds the invoice from the school's own
   * fee templates.
   */
  const handleGenerateInvoices = useCallback(async (target: { type: 'all' | 'class' | 'student', id?: string }, term: string | number, session: string, templateIds: string[], dueDate?: number) => {
    try {
      const sess = session || (schoolSettings as any).currentSession || getCurrentSession();
      if (currentUser?.role === 'student') {
        const fn = httpsCallable<{ term: number; session: string; templateIds: string[] }, { created: boolean; invoiceId?: string; reason?: string }>(getFunctions(), 'generateMyTermInvoice');
        const r = (await fn({ term: termNumber(term), session: sess, templateIds })).data;
        setToast({ message: r.created ? 'Your fees for the term are ready below.' : (r.reason || 'You already have fees for this term.'), type: r.created ? 'success' : 'info' });
        return;
      }
      let targetStudents: Student[] = [];
      const active = (s: Student) => (s.status || 'active').toLowerCase() === 'active';
      if (target.type === 'all') targetStudents = students.filter(active);
      else if (target.type === 'class' && target.id) targetStudents = students.filter(s => active(s) && s.enrollments?.some(e => e && e.classId === target.id));
      else if (target.type === 'student' && target.id) { const s = students.find(x => x.id === target.id); if (s) targetStudents = [s]; }
      if (!targetStudents.length) { setToast({ message: 'No active students found for the selected target.', type: 'error' }); return; }
      const templates = feeTemplates.filter(t => templateIds.includes(t.id));
      const r = await generateTermInvoices({
        school: financeSchool, by: financeActor, students: targetStudents, existing: invoices as any, term, session: sess,
        dueDate: dueDate || Date.now() + 21 * 86_400_000, templates, categories: feeCategories,
      });
      setToast({ message: `Invoices created for ${r.created} student${r.created === 1 ? '' : 's'} (₦${r.total.toLocaleString('en-NG')})${r.skipped.length ? `; ${r.skipped.length} already billed for this term were skipped` : ''}.`, type: r.created ? 'success' : 'info' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: e?.message || 'Error generating invoices', type: 'error' });
    }
  }, [students, invoices, feeTemplates, feeCategories, schoolSettings, currentUser, financeActor, financeSchool]);

  // Lets a student/parent drop one of their own multiple concurrent class enrollments
  // (e.g. discontinuing Islamiyya while keeping Conventional) from the Finance/Re-Enrollment view.
  const handleDropEnrollment = useCallback(async (studentId: string, classId: string) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return;
    try {
      const newEnrollments = (student.enrollments || []).filter(e => e && e.classId !== classId);
      await updateDoc(doc(db, 'students', studentId), { enrollments: newEnrollments });
    } catch (e) {
      console.error(e);
      setToast({ message: 'Could not update enrollment. Please try again.', type: 'error' });
    }
  }, [students]);

  // --- ACADEMIC POLICY: RESITS & SUMMER CLASS (shared by Admin + Teacher + Student portals) ---

  const notifyAcademicEvent = useCallback(async (opts: { studentId: string; teacherRecipientId?: string; title: string; message: string; }) => {
    const batch = writeBatch(db);
    const recipients = [opts.studentId, 'admin_broadcast', ...(opts.teacherRecipientId ? [opts.teacherRecipientId] : [])];
    recipients.forEach(recipientId => {
      const ref = doc(collection(db, 'notifications'));
      batch.set(ref, cleanData({
        id: ref.id,
        title: opts.title,
        message: opts.message,
        type: 'academic',
        recipientId,
        schoolId: currentSchool?.id,
        createdAt: Date.now(),
        readBy: []
      }));
    });
    await batch.commit();
  }, [currentSchool]);

  const handleSubmitTransferRequest = useCallback(async (student: Student, reason: string) => {
    try {
      if (!currentSchool) throw new Error('No school context');
      const enrolledClassId = student.enrollments?.[0]?.classId;
      const className = classes.find(c => c.id === enrolledClassId)?.name || '';
      await addDoc(collection(db, 'transfer_requests'), cleanData({
        schoolId: currentSchool.id,
        studentId: student.id,
        studentName: student.fullName,
        reg: student.reg,
        classId: enrolledClassId || '',
        className,
        reason,
        status: 'pending',
        requestedAt: Date.now(),
      }));
      await notifyAcademicEvent({
        studentId: student.id,
        title: 'Transfer Request Submitted',
        message: `${student.fullName} has requested a transfer. Reason: ${reason}`,
      });
      setToast({ message: 'Transfer request submitted for admin review.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error submitting transfer request: ${e?.message || 'Unknown error'}`, type: 'error' });
    }
  }, [currentSchool, classes, notifyAcademicEvent]);

  const handleCancelTransferRequest = useCallback(async (requestId: string) => {
    try {
      await updateDoc(doc(db, 'transfer_requests', requestId), { status: 'cancelled' });
      setToast({ message: 'Transfer request cancelled.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error cancelling request: ${e?.message || 'Unknown error'}`, type: 'error' });
    }
  }, []);

  const handleReviewTransferRequest = useCallback(async (requestId: string, status: 'approved' | 'rejected', adminComment?: string, conductRemark?: string) => {
    try {
      const request = transferRequests.find(r => r.id === requestId);
      if (!request) throw new Error('Transfer request not found');
      const reviewedBy = getActingUserName();
      const batch = writeBatch(db);

      if (status === 'approved') {
        const deadline = addWorkingDays(Date.now(), 5);
        batch.update(doc(db, 'transfer_requests', requestId), cleanData({
          status: 'approved', adminComment, conductRemark, reviewedAt: Date.now(), reviewedBy, deadline,
        }));
        batch.update(doc(db, 'students', request.studentId), {
          transferStatus: 'approved', activeTransferRequestId: requestId, transferDeadline: deadline,
        });
      } else {
        batch.update(doc(db, 'transfer_requests', requestId), cleanData({
          status: 'rejected', adminComment, reviewedAt: Date.now(), reviewedBy,
        }));
      }
      await batch.commit();

      await notifyAcademicEvent({
        studentId: request.studentId,
        title: status === 'approved' ? 'Transfer Request Approved' : 'Transfer Request Rejected',
        message: status === 'approved'
          ? `Your transfer request has been approved. You have 5 working days to download your Transfer Certificate and Transcript before your records are archived.`
          : `Your transfer request was rejected.${adminComment ? ` Reason: ${adminComment}` : ''}`,
      });
      setToast({ message: `Transfer request ${status}.`, type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error updating transfer request: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, [transferRequests, getActingUserName, notifyAcademicEvent]);

  const handleScheduleResit = useCallback(async (studentId: string, subjectId: string, scheduledAt: number) => {
    try {
      const student = students.find(s => s.id === studentId);
      if (!student) throw new Error('Student not found');
      
      let targetSubject = (student.subjects || []).find(s => s.id === subjectId);
      let targetSubjectName = targetSubject?.name;
      let targetTerm = targetSubject ? Number(targetSubject.term || 3) : 3;

      if (!targetSubject) {
        // Check published_results for repeating students
        const matchingPublished = publishedResults.filter(r => matchesPublishedResult(r, student, classes, sections));
        for (const pr of matchingPublished) {
          const foundInPR = pr.subjects?.find(s => s.id === subjectId || normalizeSubjectName(s.name) === normalizeSubjectName(subjectId));
          if (foundInPR) {
            targetSubject = foundInPR;
            targetSubjectName = foundInPR.name;
            targetTerm = Number(pr.term || 3);
            break;
          }
        }
      }

      if (!targetSubject && subjectId) {
        const matchingPublished = publishedResults.filter(r => matchesPublishedResult(r, student, classes, sections));
        for (const pr of matchingPublished) {
          const foundInPR = pr.subjects?.find(s => normalizeSubjectName(s.name) === normalizeSubjectName(subjectId));
          if (foundInPR) {
            targetSubject = foundInPR;
            targetSubjectName = foundInPR.name;
            targetTerm = Number(pr.term || 3);
            break;
          }
        }
      }

      if (!targetSubject) throw new Error('Subject not found for resit');

      const scheduledBy = currentUser?.id || 'unknown';
      const scheduledByName = getActingUserName();

      const updatedStudentSubjects = (student.subjects || []).map(s =>
        (s.id === subjectId || normalizeSubjectName(s.name) === normalizeSubjectName(targetSubjectName!)) ? {
          ...s,
          resitStatus: 'scheduled' as const,
          resitScheduledAt: scheduledAt,
          resitScheduledBy: scheduledBy,
          resitScheduledByName: scheduledByName,
        } : s
      );
      await updateDoc(doc(db, 'students', studentId), cleanData({ subjects: updatedStudentSubjects }));

      const matchingResults = publishedResults.filter(r =>
        matchesPublishedResult(r, student, classes, sections) && Number(r.term) === targetTerm
      );
      await Promise.all(matchingResults.map(r => {
        const idx = r.subjects.findIndex(s => s.id === subjectId || normalizeSubjectName(s.name) === normalizeSubjectName(targetSubjectName!));
        if (idx === -1) return Promise.resolve();
        const updatedResultSubjects = r.subjects.map((s, i) => i === idx ? {
          ...s,
          resitStatus: 'scheduled' as const,
          resitScheduledAt: scheduledAt,
          resitScheduledBy: scheduledBy,
          resitScheduledByName: scheduledByName,
        } : s);
        return updateDoc(doc(db, 'published_results', r.id), cleanData({ subjects: updatedResultSubjects }));
      }));

      const classTeacherId = classes.find(c => student.enrollments?.some(e => e.classId === c.id))?.teacherId;
      const when = new Date(scheduledAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
      await notifyAcademicEvent({
        studentId,
        teacherRecipientId: classTeacherId,
        title: 'Resit Scheduled',
        message: `${student.fullName}'s resit for ${targetSubjectName} has been scheduled for ${when}.`,
      });

      setToast({ message: 'Resit scheduled successfully.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error scheduling resit: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, [students, publishedResults, classes, sections, notifyAcademicEvent, currentUser, getActingUserName]);

  const handleRecordResit = useCallback(async (studentId: string, subjectId: string, newExamScore: number) => {
    try {
      const student = students.find(s => s.id === studentId);
      if (!student) throw new Error('Student not found');

      let targetSubject = (student.subjects || []).find(s => s.id === subjectId);
      let targetSubjectName = targetSubject?.name;
      let targetTerm = targetSubject ? Number(targetSubject.term || 3) : 3;

      if (!targetSubject) {
        const matchingPublished = publishedResults.filter(r => matchesPublishedResult(r, student, classes, sections));
        for (const pr of matchingPublished) {
          const foundInPR = pr.subjects?.find(s => s.id === subjectId || normalizeSubjectName(s.name) === normalizeSubjectName(subjectId));
          if (foundInPR) {
            targetSubject = foundInPR;
            targetSubjectName = foundInPR.name;
            targetTerm = Number(pr.term || 3);
            break;
          }
        }
      }

      if (!targetSubject && subjectId) {
        const matchingPublished = publishedResults.filter(r => matchesPublishedResult(r, student, classes, sections));
        for (const pr of matchingPublished) {
          const foundInPR = pr.subjects?.find(s => normalizeSubjectName(s.name) === normalizeSubjectName(subjectId));
          if (foundInPR) {
            targetSubject = foundInPR;
            targetSubjectName = foundInPR.name;
            targetTerm = Number(pr.term || 3);
            break;
          }
        }
      }

      if (!targetSubject) throw new Error('Subject not found for resit');

      const total = (targetSubject.ca1 || 0) + (targetSubject.ca2 || 0) + newExamScore;
      const { grade, remark } = computeGrade(total);
      const resitStatus: 'passed' | 'failed' = grade === 'F9' ? 'failed' : 'passed';
      const originalExam = targetSubject.originalExamBeforeResit ?? targetSubject.exam;
      const recordedBy = currentUser?.id || 'unknown';
      const recordedByName = getActingUserName();
      const recordedAt = Date.now();

      const updatedStudentSubjects = (student.subjects || []).map(s =>
        (s.id === subjectId || normalizeSubjectName(s.name) === normalizeSubjectName(targetSubjectName!)) ? {
          ...s,
          originalExamBeforeResit: originalExam,
          exam: newExamScore,
          resitStatus,
          resitScore: newExamScore,
          resitRecordedAt: recordedAt,
          resitRecordedBy: recordedBy,
          resitRecordedByName: recordedByName,
        } : s
      );

      await updateDoc(doc(db, 'students', studentId), cleanData({ subjects: updatedStudentSubjects }));

      const matchingResults = publishedResults.filter(r =>
        matchesPublishedResult(r, student, classes, sections) && Number(r.term) === targetTerm
      );
      await Promise.all(matchingResults.map(async r => {
        const idx = r.subjects.findIndex(s => s.id === subjectId || normalizeSubjectName(s.name) === normalizeSubjectName(targetSubjectName!));
        if (idx === -1) return;
        const updatedResultSubjects = r.subjects.map((s, i) => i === idx ? {
          ...s,
          exam: newExamScore,
          total,
          grade,
          remark,
          originalExamBeforeResit: originalExam,
          resitStatus,
          resitScore: newExamScore,
          resitRecordedAt: recordedAt,
          resitRecordedBy: recordedBy,
          resitRecordedByName: recordedByName,
        } : s);
        await updateDoc(doc(db, 'published_results', r.id), cleanData({ subjects: updatedResultSubjects }));
      }));

      const classTeacherId = classes.find(c => student.enrollments?.some(e => e.classId === c.id))?.teacherId;
      await notifyAcademicEvent({
        studentId,
        teacherRecipientId: classTeacherId,
        title: resitStatus === 'passed' ? 'Resit Passed' : 'Resit Recorded',
        message: `${student.fullName}'s resit for ${targetSubjectName} was recorded: ${resitStatus === 'passed' ? 'passed' : 'still not passing'} (score ${newExamScore}/60).`,
      });

      setToast({ message: `Resit recorded — ${resitStatus === 'passed' ? 'passed' : 'still failing'}.`, type: resitStatus === 'passed' ? 'success' : 'error' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error recording resit: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, [students, publishedResults, classes, sections, notifyAcademicEvent, currentUser, getActingUserName]);

  // A student who never sat a scheduled resit repeats the class and — if they hold an
  // active scholarship — permanently loses it. Mirrors the existing year-end "Mark as
  // Repeat" promotion-tool logic in AdminPortal.tsx exactly (same enrollments/session/
  // scholarship handling), but admin-triggered per missed resit rather than as a bulk
  // annual action. Never automatic — a missed resit alone doesn't repeat a class until
  // an admin explicitly confirms it here, since this is a hard-to-reverse consequence.
  const handleApplyMissedResitConsequence = useCallback(async (studentId: string, missedSubjectIds: string[]) => {
    try {
      const student = students.find(s => s.id === studentId);
      if (!student) throw new Error('Student not found');
      if (missedSubjectIds.length === 0) throw new Error('No missed subjects specified');

      const actionBy = currentUser?.id || 'unknown';
      const actionByName = getActingUserName();
      const actionAt = Date.now();
      const missedSubjectIdSet = new Set(missedSubjectIds);

      const missedSubjects = student.subjects.filter(s => missedSubjectIdSet.has(s.id));
      const classId = missedSubjects[0]?.classId || student.enrollments?.[0]?.classId;

      const updatedSubjects = student.subjects.map(s => missedSubjectIdSet.has(s.id) ? {
        ...s,
        resitStatus: 'missed' as const,
        resitMissedActionAt: actionAt,
        resitMissedActionBy: actionBy,
        resitMissedActionByName: actionByName,
      } : s);

      // Repeat: keep the student enrolled in the SAME class, roll to the current session.
      const newEnrollments = (student.enrollments || []).map(e =>
        (e && classId && e.classId === classId) ? { ...e, classId } : e
      );

      const updates: any = { subjects: updatedSubjects, enrollments: newEnrollments, session: getCurrentSession() };
      const hadActiveScholarship = !!student.scholarship?.active;
      if (hadActiveScholarship) {
        updates.scholarship = { ...student.scholarship, active: false, strippedReason: 'repeated_class', strippedAt: actionAt };
      }

      await updateDoc(doc(db, 'students', studentId), cleanData(updates));

      // Patch the matching published_results doc(s) so the existing report card reflects the missed resit too.
      const missedTerms = new Set(missedSubjects.map(s => Number(s.term)));
      const matchingResults = publishedResults.filter(r => r.studentId === studentId && missedTerms.has(Number(r.term)));
      await Promise.all(matchingResults.map(r => {
        const missedNames = new Set(missedSubjects.map(s => s.name));
        const updatedResultSubjects = r.subjects.map(s => missedNames.has(s.name) ? {
          ...s,
          resitStatus: 'missed' as const,
          resitMissedActionAt: actionAt,
          resitMissedActionBy: actionBy,
          resitMissedActionByName: actionByName,
        } : s);
        return updateDoc(doc(db, 'published_results', r.id), cleanData({ subjects: updatedResultSubjects }));
      }));

      const classTeacherId = classId ? classes.find(c => c.id === classId)?.teacherId : undefined;
      const subjectNames = missedSubjects.map(s => s.name).join(', ');
      await notifyAcademicEvent({
        studentId,
        teacherRecipientId: classTeacherId,
        title: 'Missed Resit — Repeating Class',
        message: `${student.fullName} did not sit the scheduled resit for ${subjectNames} and will repeat the class.${hadActiveScholarship ? ' Their scholarship has been ended automatically.' : ''}`,
      });

      setToast({ message: `${student.fullName} marked to repeat the class${hadActiveScholarship ? ' — scholarship ended' : ''}.`, type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error applying missed-resit consequence: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, [students, classes, publishedResults, notifyAcademicEvent, currentUser, getActingUserName]);

  // Admin-configured catalog: remedial (tied to a resit subject) or general elective
  // (Robotics, Smart Kids Farming, etc.) Summer Programs, each with its own fee.
  const handleAddSummerProgram = useCallback(async (program: Omit<SummerProgram, 'id' | 'schoolId'>) => {
    try {
      if (!currentSchool) throw new Error('No school context');
      await addDoc(collection(db, 'summer_programs'), cleanData({ ...program, schoolId: currentSchool.id }));
      setToast({ message: 'Summer Program created.', type: 'success' });
    } catch (e: any) { console.error(e); setToast({ message: `Error creating Summer Program: ${e?.message || 'Unknown error'}`, type: 'error' }); }
  }, [currentSchool]);

  const handleUpdateSummerProgram = useCallback(async (program: SummerProgram) => {
    try { await setDoc(doc(db, 'summer_programs', program.id), cleanData(program)); setToast({ message: 'Summer Program updated.', type: 'success' }); }
    catch (e: any) { console.error(e); setToast({ message: `Error updating Summer Program: ${e?.message || 'Unknown error'}`, type: 'error' }); }
  }, []);

  const handleDeleteSummerProgram = useCallback(async (id: string) => {
    try { await deleteDoc(doc(db, 'summer_programs', id)); setToast({ message: 'Summer Program deleted.', type: 'success' }); }
    catch (e: any) { console.error(e); setToast({ message: `Error deleting Summer Program: ${e?.message || 'Unknown error'}`, type: 'error' }); }
  }, []);

  // Enrolling a student generates a real Invoice using that program's fee — it lands in the
  // same `invoices` collection as tuition, so the student pays it through the existing
  // bank-transfer/proof-of-upload flow in FinanceView.tsx. No separate payment path.
  const handleOfferSummerEnrollment = useCallback(async (studentId: string, programId: string, term: number, session: string) => {
    try {
      if (!currentSchool) throw new Error('No school context');
      const student = students.find(s => s.id === studentId);
      if (!student) throw new Error('Student not found');
      const program = summerPrograms.find(p => p.id === programId);
      if (!program) throw new Error('Summer Program not found');

      const enrollmentRef = doc(collection(db, 'summer_enrollments'));
      const invoiceRef = doc(collection(db, 'invoices'));

      const batch = writeBatch(db);
      batch.set(invoiceRef, cleanData({
        studentId,
        schoolId: currentSchool.id,
        term,
        session,
        items: [{ categoryId: 'summer_program', description: `Summer Program: ${program.name}`, amount: program.fee }],
        total: program.fee,
        amountPaid: 0,
        status: 'Pending',
        createdAt: Date.now(),
        dueDate: new Date().toISOString(),
      }));
      batch.set(enrollmentRef, cleanData({
        id: enrollmentRef.id,
        schoolId: currentSchool.id,
        studentId,
        programId,
        programName: program.name,
        fee: program.fee,
        term,
        session,
        offeredAt: Date.now(),
        invoiceId: invoiceRef.id,
      }));
      await batch.commit();

      await notifyAcademicEvent({
        studentId,
        title: 'Summer Program Offered',
        message: `${student.fullName} has been offered "${program.name}" for the summer. An invoice for ${program.fee} has been generated — payable by bank transfer or your school's configured payment gateway under Finance.`,
      });

      setToast({ message: 'Summer Program offered and invoice generated.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error offering Summer Program: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, [currentSchool, students, summerPrograms, notifyAcademicEvent]);

  const handleRespondSummerEnrollment = useCallback(async (enrollmentId: string, accepted: boolean) => {
    try {
      await updateDoc(doc(db, 'summer_enrollments', enrollmentId), { accepted });
      setToast({ message: `Summer Program ${accepted ? 'accepted' : 'declined'}.`, type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error responding to Summer Program: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, []);

  const handleVerifySummerProgramSignup = useCallback(async (signupId: string) => {
    try {
      await updateDoc(doc(db, 'summer_program_signups', signupId), { paymentVerified: true });
      setToast({ message: 'Signup payment marked as verified.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error verifying signup: ${e?.message || 'Unknown error'}`, type: 'error' });
      throw e;
    }
  }, []);

  const handleMarkInvoicePaid = useCallback(async (invoiceId: string) => {
    const invoice = invoices?.find(inv => inv.id === invoiceId);
    if (!invoice) {
      setToast({ message: 'Invoice not found.', type: 'error' });
      return;
    }

    // Force strict calculation
    const total = Number(invoice.total) || 0;
    const paid = Number(invoice.amountPaid) || 0;
    const remainingBalance = total - paid;

    if (remainingBalance <= 0) {
      const newStatus = total === 0 ? 'Scholarship' : 'Paid';
      if (invoice.status !== newStatus && invoice.status !== 'Scholarship') { // Avoid redundant updates
        await updateDoc(doc(db, 'invoices', invoiceId), { status: newStatus });
        setToast({ message: `Invoice status updated to ${newStatus}.`, type: 'success' });
      } else {
        setToast({ message: `Invoice is already marked as ${newStatus}.`, type: 'info' });
      }
      return;
    }

    try {
      const paymentRef = `KCA-CASH-${Date.now().toString().slice(-6)}`;
      await addDoc(collection(db, 'payments'), {
        invoiceId,
        studentId: invoice.studentId,
        amount: remainingBalance,
        schoolId: currentSchool?.id,
        method: 'Cash',
        paidAt: Date.now(),
        paymentRef
      });

      await updateDoc(doc(db, 'invoices', invoiceId), {
        amountPaid: total,
        status: 'Paid'
      });
      setToast({ message: 'Payment recorded (Cash) and invoice marked as Paid.', type: 'success' });
    } catch (e) {
      console.error("Error marking paid:", e);
      setToast({ message: 'Failed to update invoice.', type: 'error' });
    }
  }, [invoices, currentSchool]);

  const handleDeleteInvoice = useCallback(async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'invoices', invoiceId));
      setToast({ message: 'Invoice deleted successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting invoice', type: 'error' }); }
  }, []);

  const handleUpdateInvoice = useCallback(async (invoice: Invoice) => {
    try {
      await setDoc(doc(db, 'invoices', invoice.id), invoice);
      setToast({ message: 'Invoice updated successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating invoice', type: 'error' }); }
  }, []);


  const handleRecordPayment = useCallback(async (payment: Omit<Payment, 'id' | 'paymentRef'>) => {
    const paymentRef = `KCA-${Date.now().toString().slice(-6)}`;
    if (!currentSchool) return;
    await addDoc(collection(db, 'payments'), { ...payment, paymentRef, schoolId: currentSchool.id });

    // Update invoice
    const invoice = invoices?.find(i => i.id === payment.invoiceId);
    if (invoice) {
      const newAmountPaid = invoice.amountPaid + payment.amount;
      await updateDoc(doc(db, 'invoices', invoice.id), {
        amountPaid: newAmountPaid,
        status: newAmountPaid >= invoice.total ? 'Paid' : 'Pending'
      });
    }
    setToast({ message: 'Payment recorded successfully.', type: 'success' });
  }, [invoices]);

  const handleRunPayroll = useCallback(async (payrollsToRun: Payroll[]) => {
    if (!currentSchool) return;

    let successCount = 0;
    for (const p of payrollsToRun) {
      try {
        const { id, ...data } = p; // Remove existing ID (likely empty string) to avoid overwriting doc ID
        await addDoc(collection(db, 'payrolls'), { ...data, schoolId: currentSchool.id });

        // Create Notification for the teacher
        await addDoc(collection(db, 'notifications'), {
          title: 'Payslip Generated',
          message: `Your payslip for ${new Date(p.year, p.month).toLocaleString('default', { month: 'long', year: 'numeric' })} is ready for review.`,
          type: 'finance',
          recipientId: p.teacherId, // Target the specific teacher
          createdAt: Date.now(),
          schoolId: currentSchool.id,
          read: false
        });

        successCount++;
      } catch (e) {
        console.error("Error generating payroll for", p.teacherId, e);
      }
    }
    setToast({ message: `${successCount} payslips generated successfully.`, type: 'success' });
  }, [currentSchool]);

  const handleUploadProofOfPayment = useCallback(async (invoiceId: string, proofUrl: string) => {
    await updateDoc(doc(db, 'invoices', invoiceId), { status: 'Verifying', proofOfPaymentUrl: proofUrl });
    setToast({ message: 'Proof of payment submitted for verification.', type: 'success' });
  }, []);

  const handleVerifyPayment = useCallback(async (invoiceId: string, isApproved: boolean) => {
    const invoice = invoices?.find(i => i.id === invoiceId);
    if (!invoice) return;

    if (isApproved) {
      await addDoc(collection(db, 'payments'), {
        invoiceId: invoice.id,
        studentId: invoice.studentId,
        amount: invoice.total - invoice.amountPaid,
        method: 'Admin Verification',
        schoolId: currentSchool?.id,
        paidAt: Date.now(),
        paymentRef: `VERIFIED-${invoice.id.slice(-6)}`
      });
      await updateDoc(doc(db, 'invoices', invoiceId), { status: 'Paid', amountPaid: invoice.total, proofOfPaymentUrl: null }); // null to delete field? or just ignore
      setToast({ message: 'Payment approved and invoice marked as paid.', type: 'success' });
    } else {
      await updateDoc(doc(db, 'invoices', invoiceId), { status: 'Pending', proofOfPaymentUrl: null });
      setToast({ message: 'Payment rejected. Student has been notified.', type: 'error' });
    }
  }, [invoices]);


  // --- FEE SETUP HANDLERS (Migrated) ---
  const handleAddFeeCategory = async (category: Omit<FeeCategory, 'id'>) => {
    try {
      if (!currentSchool) throw new Error("No school");
      await addDoc(collection(db, 'fee_categories'), { ...category, schoolId: currentSchool.id });
      setToast({ message: 'Fee category created.', type: 'success' });
    }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };
  const handleUpdateFeeCategory = async (category: FeeCategory) => {
    try { await setDoc(doc(db, 'fee_categories', category.id), category); setToast({ message: 'Fee category updated.', type: 'success' }); }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };
  const handleDeleteFeeCategory = async (id: string) => {
    try { await deleteDoc(doc(db, 'fee_categories', id)); setToast({ message: 'Fee category deleted.', type: 'success' }); }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };

  const handleAddFeeTemplate = async (template: Omit<FeeTemplate, 'id'>) => {
    try {
      if (!currentSchool) throw new Error("No school");
      await addDoc(collection(db, 'fee_templates'), { ...template, schoolId: currentSchool.id });
      setToast({ message: 'Fee template created.', type: 'success' });
    }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };
  const handleUpdateFeeTemplate = async (template: FeeTemplate) => {
    try { await setDoc(doc(db, 'fee_templates', template.id), template); setToast({ message: 'Fee template updated.', type: 'success' }); }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };
  const handleDeleteFeeTemplate = async (id: string) => {
    try { await deleteDoc(doc(db, 'fee_templates', id)); setToast({ message: 'Fee template deleted.', type: 'success' }); }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };

  const handleAddDiscount = async (discount: Omit<Discount, 'id'>) => {
    try {
      if (!currentSchool) throw new Error("No school");
      await addDoc(collection(db, 'discounts'), { ...discount, schoolId: currentSchool.id });
      setToast({ message: 'Discount created.', type: 'success' });
    }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };
  const handleUpdateDiscount = async (discount: Discount) => {
    try { await setDoc(doc(db, 'discounts', discount.id), discount); setToast({ message: 'Discount updated.', type: 'success' }); }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };
  const handleDeleteDiscount = async (id: string) => {
    try { await deleteDoc(doc(db, 'discounts', id)); setToast({ message: 'Discount deleted.', type: 'success' }); }
    catch (e) { console.error(e); setToast({ message: 'Error', type: 'error' }); }
  };

  // --- SALARY ADVANCE HANDLERS ---
  const handleAddSalaryAdvance = useCallback(async (request: Omit<SalaryAdvanceRequest, 'id' | 'status' | 'requestDate' | 'repaidAmount'>) => {
    try {
      if (!currentSchool) return;
      await addDoc(collection(db, 'salary_advances'), {
        ...request,
        schoolId: currentSchool.id,
        status: 'Pending',
        requestDate: Date.now(),
        repaidAmount: 0
      });
      setToast({ message: 'Salary advance requested successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error requesting advance', type: 'error' }); }
  }, [currentSchool]);

  const handleUpdateSalaryAdvance = useCallback(async (id: string, updates: Partial<SalaryAdvanceRequest>) => {
    try {
      await updateDoc(doc(db, 'salary_advances', id), updates);
      setToast({ message: 'Salary advance status updated.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating advance', type: 'error' }); }
  }, []);

  // --- SCHOOL SETTINGS HANDLER ---
  const handleUpdateSchoolSettings = useCallback(async (settings: SchoolSettings) => {
    try {
      // Payment gateway SECRET keys must never sit in the school settings (anyone can read those):
      // move any that are there into the school's secure payment settings, then leave them out.
      if (settings.paymentGateways) {
        const save = httpsCallable(getFunctions(), 'saveSchoolPaymentConfig');
        const gw: any = { ...settings.paymentGateways };
        for (const g of ['stripe', 'paystack', 'flutterwave'] as const) {
          const k = gw[g]?.secretKey;
          if (k) {
            await save({ schoolId: currentSchool?.id || 'kca-main', gateway: g, secretKey: k, ...(gw[g]?.publicKey ? { publicKey: gw[g].publicKey } : {}) });
            const { secretKey: _drop, ...rest } = gw[g];
            gw[g] = rest;
          }
        }
        settings = { ...settings, paymentGateways: gw };
      }
      if (!currentSchool) throw new Error("No school context");

      // Never let a save blank out the logo, signature or stamp: if the page that saved
      // didn't have them (e.g. it opened before the school's settings finished loading),
      // keep the ones already on file.
      const kept = { ...settings } as any;
      for (const k of ['logoDataUrl', 'headOfSchoolSignatureUrl', 'schoolStampUrl'] as const) {
        if (!kept[k] && (contextSchoolSettings as any)?.[k]) kept[k] = (contextSchoolSettings as any)[k];
      }
      const cleanSettings = cleanData(kept);

      // 1. Update Tenant Document (Primary Source of Truth for Multi-Tenancy)
      await setDoc(doc(db, 'schools', currentSchool.id), {
        settings: cleanSettings,
        // Update top-level fields that mirror settings for easier querying
        name: settings.schoolName
      }, { merge: true });

      // 2. Legacy Sync (Only for main school to keep existing code working if strictly needed)
      if (currentSchool.id === 'kca-main') {
        await setDoc(doc(db, 'settings', 'school'), cleanSettings, { merge: true });
      }

      setSchoolSettings(kept); // keep in-memory state in sync immediately
      setToast({ message: 'Settings saved successfully.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      const tooLarge = /exceeds the maximum|too large|1048576/i.test(e?.message || '');
      setToast({
        message: tooLarge
          ? 'Settings are too large to save — please re-upload the logo, signature, stamp and partner logos (they will be resized automatically).'
          : `Error updating settings: ${e?.message || 'Unknown error'}`,
        type: 'error'
      });
    }
  }, [currentSchool, contextSchoolSettings]);




  // --- NON-ACADEMIC STAFF & OPERATIONS HANDLERS ---
  const handleAddNonAcademicStaff = useCallback(async (staff: Omit<NonAcademicStaffAccount, 'role' | 'active'>) => {
    if (nonAcademicStaff.some(s => s.username === staff.username)) {
      setToast({ message: `Staff with username "${staff.username}" already exists. Please use a unique username (e.g., ${staff.username}2).`, type: 'error' });
      return;
    }
    try {
      if (!currentSchool) throw new Error("No school context");
      await setDoc(doc(db, 'non_academic_staff', staff.username), cleanData({
        ...staff,
        role: 'non-academic',
        active: true,
        schoolId: currentSchool.id
      }));
      setToast({ message: `Staff member ${staff.fullName} created successfully.`, type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error creating staff', type: 'error' }); }
  }, [nonAcademicStaff, currentSchool]);

  const handleUpdateNonAcademicStaff = useCallback(async (staff: NonAcademicStaffAccount) => {
    try {
      await setDoc(doc(db, 'non_academic_staff', staff.username), cleanData(staff));
      setToast({ message: `Staff member ${staff.fullName} updated successfully.`, type: 'success' });
    } catch (e: any) {
      console.error("Error updating staff:", e);
      setToast({ message: `Error updating staff: ${e.message || 'Unknown error'}`, type: 'error' });
    }
  }, []);

  const handleDeleteNonAcademicStaff = useCallback(async (username: string) => {
    try {
      await deleteDoc(doc(db, 'non_academic_staff', username));
      setToast({ message: 'Staff record deleted.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting staff', type: 'error' }); }
  }, []);

  // --- ADMIN MANAGEMENT HANDLERS ---
  const handleAddAdmin = useCallback(async (admin: Omit<AdminAccount, 'id'>) => {
    try {
      const { password, ...adminWithoutPassword } = admin as any;
      await setDoc(doc(db, 'admins', admin.username), { ...adminWithoutPassword, id: admin.username });
      if (admin.email && password) {
        await createFirebaseAuthUser(admin.email, password);
      }
      setToast({ message: 'Admin added successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error adding admin', type: 'error' }); }
  }, []);

  const handleUpdateAdmin = useCallback(async (admin: AdminAccount) => {
    try {
      const { password, ...adminWithoutPassword } = admin as any;
      await updateDoc(doc(db, 'admins', admin.id), adminWithoutPassword as any);
      if (password) {
        const updateAuthPass = httpsCallable(getFunctions(), 'updateUserAuthPassword');
        await updateAuthPass({ email: admin.email, newPassword: password });
      }
      setToast({ message: 'Admin updated successfully.', type: 'success' });
    } catch (e: any) {
      console.error(e);
      setToast({ message: `Error updating admin: ${e.message || 'Unknown error'}`, type: 'error' });
    }
  }, []);

  const handleDeleteAdmin = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'admins', id));
      setToast({ message: 'Admin deleted successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting admin', type: 'error' }); }
  }, []);

  const handleAddIssueReport = useCallback(async (report: Omit<IssueReport, 'id' | 'reportedAt' | 'status'>) => {
    try {
      await addDoc(collection(db, 'issue_reports'), {
        ...report,
        reportedAt: Date.now(),
        status: 'Reported',
      });
      setToast({ message: 'Issue reported successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error reporting issue', type: 'error' }); }
  }, []);

  const handleUpdateIssueReport = useCallback(async (reportId: string, status: IssueStatus, assignedToId?: string, adminNotes?: string) => {
    try {
      const updates: any = { status };
      if (assignedToId !== undefined) updates.assignedToId = assignedToId;
      if (adminNotes !== undefined) updates.adminNotes = adminNotes;
      await updateDoc(doc(db, 'issue_reports', reportId), updates);
      setToast({ message: 'Issue report updated.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating issue', type: 'error' }); }
  }, []);

  const handleClockInOut = useCallback(async (staffId: string) => {
    // Determine user type and details
    const teacher = teachers.find(t => t.username === staffId);
    const staff = nonAcademicStaff.find(s => s.username === staffId);

    const staffName = teacher ? teacher.fullName : (staff ? staff.fullName : 'Unknown Staff');
    const role: 'teacher' | 'non-academic' = teacher ? 'teacher' : 'non-academic';
    const department = staff ? staff.department : undefined; // Teachers don't have department in same way, or use section?

    const activeRecord = clockInRecords?.find(r => r.staffId === staffId && !r.clockOutTime);

    // TIME CALCULATIONS
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    const timeInMinutes = h * 60 + m;

    // ISLAMIYYA CHECK
    let isIslamiyya = false;
    if (teacher && teacher.sectionIds && sections.length > 0) {
      isIslamiyya = teacher.sectionIds.some(id => {
        const sectionName = sections.find(s => s.id === id)?.name.toLowerCase();
        return sectionName?.includes('islamiyya') || sectionName?.includes('tahfiz') || sectionName?.includes('arabic');
      });
    }

    // DETERMINE STATUS
    let status: 'late' | 'ontime' = 'ontime';
    let lateThreshold = 7 * 60 + 30; // 7:30 AM default

    if (isIslamiyya) {
      lateThreshold = 13 * 60 + 30; // 1:30 PM
    }

    if (timeInMinutes > lateThreshold) {
      status = 'late';
    }

    try {
      if (activeRecord) {
        await updateDoc(doc(db, 'clock_in_records', activeRecord.id), { clockOutTime: Date.now() });
        setToast({ message: 'Clocked out successfully.', type: 'success' });
      } else {
        await addDoc(collection(db, 'clock_in_records'), cleanData({
          staffId,
          staffName,
          role,
          department,
          schoolId: currentSchool?.id,
          clockInTime: Date.now()
        }));
        console.log("DEBUG: Clock-In Record added for", staffName);
        setToast({ message: 'Clocked in successfully.', type: 'success' });

        // NOTIFICATION LOGIC
        const notificationMsg = status === 'late'
          ? `Hello ${staffName}, you have clocked in late today. Please endeavor to be punctual.`
          : `Good morning ${staffName}, you are on time! Have a productive day.`;

        await addDoc(collection(db, 'notifications'), cleanData({
          title: status === 'late' ? 'Lateness Alert' : 'Clock-In Confirmation',
          message: notificationMsg,
          type: 'system',
          recipientId: staffId,
          createdAt: Date.now(),
          readBy: [],
          schoolId: currentSchool?.id
        }));

        // SYSTEM MESSAGE LOGIC
        try {
          const systemConvRef = collection(db, 'conversations');
          const q = query(systemConvRef, where('participants', 'array-contains', staffId), where('type', '==', 'system'));
          const snap = await getDocs(q);

          let convId;
          if (snap.empty) {
            const newConv = await addDoc(systemConvRef, cleanData({
              participants: [staffId],
              type: 'system',
              active: true,
              updatedAt: Date.now(),
              unreadCounts: { [staffId]: 1 },
              lastMessage: { content: notificationMsg, senderId: 'SYSTEM', createdAt: Date.now(), type: 'text' }
            }));
            convId = newConv.id;
          } else {
            convId = snap.docs[0].id;
            await updateDoc(doc(db, 'conversations', convId), {
              lastMessage: { content: notificationMsg, senderId: 'SYSTEM', createdAt: Date.now(), type: 'text' },
              updatedAt: Date.now(),
              [`unreadCounts.${staffId}`]: increment(1)
            });
          }

          await addDoc(collection(db, `conversations/${convId}/messages`), cleanData({
            senderId: 'SYSTEM',
            content: notificationMsg,
            type: 'system',
            createdAt: Date.now(),
            readBy: []
          }));
        } catch (e) {
          console.error("Error creating system message:", e);
        }
      }
    } catch (e) {
      console.error("ClockIn Error Details:", e);
      setToast({ message: 'Error clocking in/out', type: 'error' });
    }
  }, [clockInRecords, teachers, nonAcademicStaff, sections, currentSchool]);

  // --- IQA SAMPLING & OBSERVATION HANDLERS ---
  const handleAddOrUpdateSamplingEntry = useCallback(async (entry: Omit<IQASamplingPlanEntry, 'id'> | IQASamplingPlanEntry) => {
    try {
      if ('id' in entry) {
        await setDoc(doc(db, 'iqa_sampling_plan', entry.id), entry);
        setToast({ message: 'Sampling plan entry updated.', type: 'success' });
      } else {
        await addDoc(collection(db, 'iqa_sampling_plan'), entry);
        setToast({ message: 'New sampling plan entry added.', type: 'success' });
      }
    } catch (e) { console.error(e); setToast({ message: 'Error updating sampling plan', type: 'error' }); }
  }, []);

  const handleDeleteSamplingEntry = useCallback(async (entryId: string) => {
    try {
      await deleteDoc(doc(db, 'iqa_sampling_plan', entryId));
      setToast({ message: 'Sampling plan entry deleted.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting sampling plan', type: 'error' }); }
  }, []);

  const handleAddOrUpdateObservation = useCallback(async (observation: Omit<Observation, 'id' | 'observerId'> | Observation) => {
    try {
      if (!currentSchool) throw new Error("No school context");

      // Sanitize data: remove undefined values or convert to null
      const sanitizedObs = JSON.parse(JSON.stringify(observation));

      if ('id' in observation) {
        await setDoc(doc(db, 'observations', observation.id), sanitizedObs);
        setToast({ message: 'Observation updated successfully.', type: 'success' });
      } else {
        await addDoc(collection(db, 'observations'), { ...sanitizedObs, observerId: 'admin-user', schoolId: currentSchool.id });
        setToast({ message: 'Observation scheduled successfully.', type: 'success' });
      }
    } catch (e) { console.error(e); setToast({ message: 'Error updating observation', type: 'error' }); }
  }, [currentSchool]);

  // --- ACTION PLAN HANDLERS ---
  const handleAddActionPlanItem = useCallback(async (item: Omit<ActionPlanItem, 'id' | 'createdAt' | 'status' | 'comments'>) => {
    try {
      await addDoc(collection(db, 'action_plan_items'), {
        ...item,
        createdAt: Date.now(),
        status: 'Open',
        comments: [],
      });
      setToast({ message: 'Action point created and assigned.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error creating action plan', type: 'error' }); }
  }, []);

  type ActionPlanUpdatePayload = Partial<Omit<ActionPlanItem, 'id' | 'comments'>> & { newCommentText?: string };

  const handleUpdateActionPlanItem = useCallback(async (itemId: string, updates: ActionPlanUpdatePayload, author: User) => {
    const item = actionPlanItems?.find(i => i.id === itemId);
    if (!item) return;

    try {
      const { newCommentText, ...restOfUpdates } = updates;
      const updatedItem = { ...item, ...restOfUpdates };
      if (newCommentText) {
        const authorAccount = author.role === 'teacher' ? teachers?.find(t => t.username === author.id) : (author.role === 'admin' ? { fullName: 'Admin' } : null);
        const newComment: ActionPlanItemComment = {
          authorId: author.id,
          authorName: authorAccount?.fullName || 'System',
          text: newCommentText,
          createdAt: Date.now(),
        };
        updatedItem.comments = [...item.comments, newComment];
      }

      await setDoc(doc(db, 'action_plan_items', itemId), updatedItem);
      setToast({ message: 'Action point updated.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating action plan', type: 'error' }); }
  }, [actionPlanItems, teachers]);

  // --- STANDARDISATION HUB HANDLERS ---
  const handleAddStandardisationFile = useCallback(async (file: Omit<StandardisationFile, 'id'>) => {
    try {
      await addDoc(collection(db, 'standardisation_files'), file);
      setToast({ message: 'File uploaded to Standardisation Hub.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error uploading file', type: 'error' }); }
  }, []);

  const handleDeleteStandardisationFile = useCallback(async (fileId: string) => {
    try {
      await deleteDoc(doc(db, 'standardisation_files', fileId));
      setToast({ message: 'File deleted from Standardisation Hub.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting file', type: 'error' }); }
  }, []);

  // --- CBT HANDLERS ---
  const handleAddCBTTest = useCallback(async (test: Omit<CBT_Test, 'id' | 'createdAt' | 'status'>) => {
    try {
      await addDoc(collection(db, 'cbt_tests'), {
        ...test,
        createdAt: Date.now(),
        status: 'draft'
      });
      setToast({ message: 'CBT Test created successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error creating test', type: 'error' }); }
  }, []);

  const handleUpdateCBTTest = useCallback(async (testId: string, updates: Partial<CBT_Test>) => {
    try {
      await updateDoc(doc(db, 'cbt_tests', testId), updates);
      setToast({ message: 'CBT Test updated.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating test', type: 'error' }); }
  }, []);

  const handleDeleteCBTTest = useCallback(async (testId: string) => {
    try {
      await deleteDoc(doc(db, 'cbt_tests', testId));
      setToast({ message: 'CBT Test deleted.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error deleting test', type: 'error' }); }
  }, []);

  // --- STUDENT FEEDBACK HANDLERS ---
  const handleAddStudentFeedback = useCallback(async (feedback: Omit<StudentFeedback, 'id' | 'submittedAt' | 'status'>) => {
    try {
      await addDoc(collection(db, 'student_feedback'), {
        ...feedback,
        submittedAt: Date.now(),
        status: 'Submitted',
      });
      setToast({ message: 'Your feedback has been submitted successfully.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error submitting feedback', type: 'error' }); }
  }, []);

  const handleUpdateStudentFeedback = useCallback(async (feedbackId: string, status: FeedbackStatus, adminResponse?: string) => {
    try {
      const updates: any = { status };
      if (adminResponse) updates.adminResponse = adminResponse;
      if (status === 'Resolved' || status === 'Closed') updates.resolvedAt = Date.now();

      await updateDoc(doc(db, 'student_feedback', feedbackId), updates);
      setToast({ message: 'Feedback has been updated.', type: 'success' });
    } catch (e) { console.error(e); setToast({ message: 'Error updating feedback', type: 'error' }); }
  }, []);

  // --- GENERAL APP HANDLERS & AUTH ---
  const handleUpdateAdminPassword = useCallback(async (currentPass: string, newPass: string) => {
    if (!auth.currentUser) {
      setToast({ message: 'No active user found.', type: 'error' });
      return false;
    }

    try {
      // 1. Re-authenticate
      const credential = EmailAuthProvider.credential(auth.currentUser.email!, currentPass);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // 2. Update Password
      await updatePassword(auth.currentUser, newPass);

      setToast({ message: 'Password updated successfully.', type: 'success' });
      return true;
    } catch (error: any) {
      console.error("Password Update Error:", error);
      let msg = 'Failed to update password.';
      if (error.code === 'auth/wrong-password') msg = 'Current password is incorrect.';
      if (error.code === 'auth/weak-password') msg = 'New password is too weak.';

      setToast({ message: msg, type: 'error' });
      return false;
    }
  }, []);

  const handleLogout = useCallback(() => {
    manualLogoutRef.current = true;
    if (auth) auth.signOut();
    setCurrentUser(null);
    setActivePortal(Portal.LANDING);
    setToast({ message: 'Logged out successfully.', type: 'success' });
  }, []);

  // --- AUTO-LOGOUT ---
  useEffect(() => {
    if (!currentUser) return;

    let timeoutId: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        handleLogout();
        setToast({ message: 'Logged out due to inactivity', type: 'error' });
      }, 60 * 60 * 1000); // 1 hour
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetTimer));

    resetTimer(); // Start timer

    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => window.removeEventListener(event, resetTimer));
    };
  }, [currentUser, handleLogout]);

  const handleLogin = useCallback(async (email: string, password: string) => {
    try {
      // --- STANDARD AUTHENTICATION ---
      const cleanEmail = email.toLowerCase().trim();

      // Firebase Auth (The Source of Truth)
      if (auth) {
        isHandlingLoginRef.current = true;
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        const userEmail = user.email;

        if (userEmail) {
          const inputEmail = userEmail.toLowerCase();

          // ── Hardcoded admin shortcuts (instant, no Firestore) ─────────────
          if (inputEmail === 'sadmin@kdcambridge.com') {
            setCurrentUser({
              id: 'admin-user',
              role: 'super_admin',
              email: userEmail,
              permissions: ['manage_students', 'manage_teachers', 'manage_classes', 'manage_finance', 'manage_timetable', 'manage_cbt', 'manage_settings', 'manage_admins', 'manage_non_academic']
            });
            setActivePortal(Portal.SUPER_ADMIN);
            setToast({ message: 'Welcome back, Super Admin.', type: 'success' });
            return;
          }

          // 1.b Main School Admin (Emergency Access / Hardcoded for admin@kdcambridge.com)
          if (inputEmail === 'admin@kdcambridge.com' || inputEmail === 'admin') {
            setCurrentUser({
              id: 'school-admin-main',
              role: 'admin',
              email: userEmail,
              permissions: ['manage_students', 'manage_teachers', 'manage_classes', 'manage_finance', 'manage_timetable', 'manage_fees', 'manage_reports', 'manage_settings', 'manage_admins', 'manage_non_academic', 'manage_cbt', 'manage_iqa', 'manage_observations'],
              schoolId: 'kca-main'
            });
            setActivePortal(Portal.ADMIN);
            setToast({ message: 'Welcome back, School Admin.', type: 'success' });
            return;
          }

          // ── Fire hint getDoc + full email queries IN PARALLEL ─────────────
          // Hint check is a single read (~200 ms). The 4-query search takes ~800 ms.
          // Running them concurrently means repeat logins finish at 200 ms + auth time,
          // and first-time logins finish at 800 ms + auth time — not 200 + 800.
          const HINT_KEY = 'kca_session_hint';
          const searchEmails = Array.from(new Set([inputEmail, userEmail]));

          let parsedHint: any = null;
          try { parsedHint = JSON.parse(localStorage.getItem(HINT_KEY) || 'null'); } catch {}
          const validHint = parsedHint?.uid === user.uid && parsedHint.col && parsedHint.docId;

          const hintPromise = validHint
            ? withTimeout(getDoc(doc(db, parsedHint.col, parsedHint.docId)), 10000, null)
            : Promise.resolve(null);

          // Each lookup on its own: one refused collection must not hide the others.
          const safeLookup = (col: string) => getDocs(query(collection(db, col), where('email', 'in', searchEmails)))
            .catch(err => { console.warn(`Login lookup in ${col} failed:`, err?.code || err); return emptyQuerySnapshot; });
          const emailQueriesPromise = Promise.all([safeLookup('admins'), safeLookup('teachers'), safeLookup('students'), safeLookup('non_academic_staff')]);

          // ── Hint path (typically resolves first) ──────────────────────────
          const hintSnap = await hintPromise;
          if (hintSnap?.exists()) {
            const d = hintSnap.data();
            if (parsedHint.col === 'students') {
              const s = { id: hintSnap.id, ...d } as Student;
              setCurrentUser({ id: s.id, email: userEmail, role: 'student', data: s, schoolId: s.schoolId });
              setActivePortal(s.isExternal ? Portal.EXTERNAL_STUDENT : Portal.STUDENT);
              setToast({ message: `Welcome back, ${s.firstName}`, type: 'success' });
              return;
            } else if (parsedHint.col === 'teachers') {
              const t = { id: hintSnap.id, ...d } as any;
              setCurrentUser({ id: t.username || hintSnap.id, role: 'teacher', email: userEmail, data: t, schoolId: t.schoolId || 'kca-main' });
              setActivePortal(Portal.TEACHER);
              setToast({ message: `Welcome back, ${t.fullName}`, type: 'success' });
              return;
            } else if (parsedHint.col === 'admins') {
              const a = { id: hintSnap.id, ...d } as AdminAccount;
              if (a.active) {
                setCurrentUser({ id: a.username, role: 'admin', email: userEmail, permissions: a.permissions, schoolId: a.schoolId });
                setActivePortal(Portal.ADMIN);
                setToast({ message: `Welcome back, ${a.fullName}`, type: 'success' });
                return;
              }
            } else if (parsedHint.col === 'non_academic_staff') {
              const st = { id: hintSnap.id, ...d } as any;
              setCurrentUser({ id: st.username || hintSnap.id, role: 'non-academic', email: userEmail, data: st, schoolId: st.schoolId });
              setActivePortal(Portal.NON_ACADEMIC);
              setToast({ message: `Welcome back, ${st.fullName}`, type: 'success' });
              return;
            }
            // Hint doc existed but role didn't match — clear stale hint & fall through
            try { localStorage.removeItem(HINT_KEY); } catch {}
          }

          // ── Email queries (already running, likely done by now) ────────────
          // On a slow connection (or a database error) the lookup returns this marker instead of
          // results — the student must then be told to try again, not that they have no profile.
          const lookupFailed = [emptyQuerySnapshot, emptyQuerySnapshot, emptyQuerySnapshot, emptyQuerySnapshot];
          const lookup = await withTimeout(emailQueriesPromise, 45000, lookupFailed);
          if (lookup === lookupFailed) {
            await auth.signOut();
            throw Object.assign(new Error('The connection is slow and your profile could not be loaded. Please check your internet and press Sign In again.'), { code: 'auth/slow-network' });
          }
          const [adminSnap, teacherSnap, studentSnap, staffSnap] = lookup;

          if (!adminSnap.empty) {
            const adminData = { id: adminSnap.docs[0].id, ...adminSnap.docs[0].data() } as AdminAccount;
            if (adminData.active) {
              setCurrentUser({ id: adminData.username, role: 'admin', email: userEmail, permissions: adminData.permissions, schoolId: adminData.schoolId });
              setActivePortal(Portal.ADMIN);
              try { localStorage.setItem(HINT_KEY, JSON.stringify({ uid: user.uid, col: 'admins', docId: adminSnap.docs[0].id })); } catch {}
              setToast({ message: `Welcome back, ${adminData.fullName}`, type: 'success' });
              return;
            }
            setToast({ message: 'Account is inactive. Contact Super Admin.', type: 'error' });
            return;
          }

          // Teachers — email query first, then parallel ID-based fallback
          let teacherData: TeacherAccount | null = null;
          if (!teacherSnap.empty) {
            teacherData = { id: teacherSnap.docs[0].id, ...teacherSnap.docs[0].data() } as unknown as TeacherAccount;
          } else {
            const uname = userEmail.split('@')[0];
            const ids = Array.from(new Set([uname, uname.toLowerCase(), uname.charAt(0).toUpperCase() + uname.slice(1).toLowerCase()]));
            const fallbacks = await withTimeout(
              Promise.all(ids.map(id => getDoc(doc(db, 'teachers', id)))),
              10000,
              ids.map(() => null as any)
            );
            const found = fallbacks.find(s => s && s.exists());
            if (found) teacherData = { id: found.id, ...found.data() } as unknown as TeacherAccount;
          }
          if (teacherData) {
            setCurrentUser({ id: teacherData.username || (teacherData as any).id, role: 'teacher', email: userEmail, data: teacherData, schoolId: teacherData.schoolId || 'kca-main' });
            setActivePortal(Portal.TEACHER);
            try { localStorage.setItem(HINT_KEY, JSON.stringify({ uid: user.uid, col: 'teachers', docId: (teacherData as any).id })); } catch {}
            setToast({ message: `Welcome back, ${teacherData.fullName}`, type: 'success' });

            // Self-healing: Update missing email field in Firestore
            if (!teacherData.email) {
              console.log(`Self-healing (Login): Writing email for teacher: ${teacherData.id}`);
              updateDoc(doc(db, 'teachers', teacherData.id), { email: userEmail }).catch((err) => {
                console.error("Self-healing error updating teacher email during login:", err);
              });
            }
            return;
          }

          // Students — email query first, then fallback search
          let studentData: Student | null = null;
          if (!studentSnap.empty) {
            studentData = { id: studentSnap.docs[0].id, ...studentSnap.docs[0].data() } as Student;
          } else {
            studentData = await findStudentFallback(userEmail, currentSchool?.id);
          }

          if (studentData) {
            setCurrentUser({ id: studentData.id, email: userEmail, role: 'student', data: studentData, schoolId: studentData.schoolId });
            setActivePortal(studentData.isExternal ? Portal.EXTERNAL_STUDENT : Portal.STUDENT);
            try { localStorage.setItem(HINT_KEY, JSON.stringify({ uid: user.uid, col: 'students', docId: studentData.id })); } catch {}
            setToast({ message: `Welcome back, ${studentData.firstName}`, type: 'success' });

            // Self-healing: Update missing email field in Firestore
            if (!studentData.email) {
              console.log(`Self-healing (Login): Writing email for student: ${studentData.id}`);
              updateDoc(doc(db, 'students', studentData.id), { email: userEmail }).catch((err) => {
                console.error("Self-healing error updating student email during login:", err);
              });
            }
            return;
          }

          // Non-Academic Staff
          if (!staffSnap.empty) {
            const staffData = { id: staffSnap.docs[0].id, ...staffSnap.docs[0].data() } as unknown as NonAcademicStaffAccount;
            const staffId = staffData.username || staffSnap.docs[0].id;
            setCurrentUser({ id: staffId, role: 'non-academic', email: userEmail, data: staffData, schoolId: staffData.schoolId });
            setActivePortal(Portal.NON_ACADEMIC);
            try { localStorage.setItem(HINT_KEY, JSON.stringify({ uid: user.uid, col: 'non_academic_staff', docId: staffSnap.docs[0].id })); } catch {}
            setToast({ message: `Welcome back, ${staffData.fullName}`, type: 'success' });
            return;
          }

          // No profile matched — sign out and surface a clear error so the student sees it inline
          await auth.signOut();
          throw Object.assign(new Error('No student or staff profile found for this email. Contact your administrator.'), { code: 'auth/no-profile' });
        }
      }
    } catch (error: any) {
      console.error("Login error:", error);

      let msg = 'Login failed. Please check your credentials.';
      if (error.code === 'auth/user-not-found') msg = 'No account found with this email address.';
      if (error.code === 'auth/wrong-password') msg = 'Incorrect password. Please try again.';
      if (error.code === 'auth/invalid-credential') msg = 'Incorrect email or password. Please try again.';
      if (error.code === 'auth/invalid-email') msg = 'Invalid email address format.';
      if (error.code === 'auth/too-many-requests') msg = 'Too many failed attempts. Please wait a few minutes and try again.';
      if (error.code === 'auth/network-request-failed') msg = 'Network error. Please check your internet connection and try again.';
      if (error.code === 'auth/no-profile' || error.code === 'auth/slow-network') msg = error.message;

      // Append error code for visibility
      setToast({ message: `${msg} (${error.code || 'Unknown Error'})`, type: 'error' });
      // Re-throw so the login form can display the message inline
      throw new Error(msg);
    }
  }, [students, teachers, admins, nonAcademicStaff]);



  const handleResetData = useCallback(() => {
    if (window.confirm('Are you sure you want to reset ALL application data? This action is irreversible and will restore the portal to its original demo state.')) {
      localStorage.clear();
      window.location.reload();
    }
  }, []);

  const handleFactoryReset = useCallback(async () => {
    if (window.confirm('⚠️ CRITICAL WARNING ⚠️\n\nThis will PERMANENTLY DELETE ALL DATA from the database (Students, Teachers, Classes, Finance, etc.).\n\nThis action cannot be undone. Are you sure you want to proceed?')) {
      if (window.confirm('Final Confirmation: Type "DELETE" to confirm (mentally) and click OK to wipe everything.')) {
        setLoading(true);
        try {
          const collections = [
            'students', 'teachers', 'classes', 'sections', 'invoices', 'payments',
            'published_results', 'attendance_submissions', 'iqa_submissions', 'announcements',
            'student_feedback', 'non_academic_staff', 'issue_reports', 'clock_in_records',
            'payrolls', 'salary_advances', 'expense_claims', 'observations', 'action_plan_items',
            'standardisation_files', 'reviews', 'cbt_tests', 'fee_categories', 'fee_templates',
            'discounts', 'iqa_sampling_plan'
          ];

          for (const colName of collections) {
            const q = query(collection(db, colName));
            const snapshot = await getDocs(q);
            const batch = writeBatch(db);
            let count = 0;

            for (const doc of snapshot.docs) {
              batch.delete(doc.ref);
              count++;
              if (count >= 400) { // Firestore batch limit is 500
                await batch.commit();
                count = 0;
              }
            }
            if (count > 0) await batch.commit();
          }

          alert('Factory Reset Complete. The application will now reload.');
          window.location.reload();
        } catch (error) {
          console.error("Factory Reset Failed:", error);
          alert('Factory Reset Failed. Check console for details.');
          setLoading(false);
        }
      }
    }
  }, []);



  const renderPortal = () => {
    switch (activePortal) {
      case Portal.SUPER_ADMIN:
        return (
          <ErrorBoundary>
            <SuperAdminDashboard
              onLogout={handleLogout}
              onViewSchoolDashboard={(schoolId) => {
                switchSchool(schoolId);
                setActivePortal(Portal.ADMIN);
                setToast({ message: `Viewing Dashboard for ${schoolId}`, type: 'success' });
              }}
            />
          </ErrorBoundary>
        );
      case Portal.ADMIN:
        return (
          <ErrorBoundary>
            <AdminPortal
              user={currentUser!}
              onLogout={handleLogout}
              unreadMessageCount={unreadMessageCount}
              schoolSettings={schoolSettings}
              onUpdateSchoolSettings={handleUpdateSchoolSettings}
              onUpdateAdminPassword={handleUpdateAdminPassword}
              onResetData={handleFactoryReset}
              admins={admins}
              onAddAdmin={handleAddAdmin}
              onUpdateAdmin={handleUpdateAdmin}
              onDeleteAdmin={handleDeleteAdmin}
              students={students}
              teachers={teachers}
              classes={classes}
              sections={sections}
              invoices={invoices}
              payments={payments}
              transferRequests={transferRequests}
              onReviewTransferRequest={handleReviewTransferRequest}
              archivedStudents={archivedStudents}
              onAddStudent={handleAddStudent}
              onUpdateStudent={async (s) => {
                const cleanS = { ...s, email: s.email ? s.email.toLowerCase().trim() : s.email };
                await updateDoc(doc(db, 'students', s.id), cleanData(cleanS));
                setToast({ message: 'Student updated', type: 'success' });
              }}
              onDeleteStudent={async (id) => { await deleteDoc(doc(db, 'students', id)); setToast({ message: 'Student deleted', type: 'success' }); }}
              onAddTeacher={handleAddTeacher}
              onUpdateTeacher={handleUpdateTeacher}
              onToggleTeacherActive={handleToggleTeacherActive}
              onDeleteTeacher={handleDeleteTeacher}
              onAddClass={async (c) => {
                const newClass = { ...c, schoolId: currentSchool?.id };
                await setDoc(doc(db, 'classes', c.id), cleanData(newClass));
                setToast({ message: 'Class added', type: 'success' });
              }}
              onUpdateClass={async (c) => { await updateDoc(doc(db, 'classes', c.id), cleanData(c)); setToast({ message: 'Class updated', type: 'success' }); }}
              onDeleteClass={async (id) => { await deleteDoc(doc(db, 'classes', id)); setToast({ message: 'Class deleted', type: 'success' }); }}
              onAddSection={async (s) => {
                const id = (s as any).id || `sec_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
                const newSection = { ...s, id, schoolId: currentSchool?.id };
                await setDoc(doc(db, 'sections', id), cleanData(newSection));
                setToast({ message: 'Section added', type: 'success' });
              }}
              onUpdateSection={async (s) => { await updateDoc(doc(db, 'sections', s.id), cleanData(s)); setToast({ message: 'Section updated', type: 'success' }); }}
              onDeleteSection={async (id) => { await deleteDoc(doc(db, 'sections', id)); setToast({ message: 'Section deleted', type: 'success' }); }}
              onGenerateInvoices={handleGenerateInvoices}
              onAddInvoice={async (i) => { await setDoc(doc(db, 'invoices', i.id), cleanData(i)); setToast({ message: 'Invoice added', type: 'success' }); }}
              onUpdateInvoice={async (i) => { await updateDoc(doc(db, 'invoices', i.id), cleanData(i)); setToast({ message: 'Invoice updated', type: 'success' }); }}
              onDeleteInvoice={async (id) => { await deleteDoc(doc(db, 'invoices', id)); setToast({ message: 'Invoice deleted', type: 'success' }); }}
              onAddPayment={async (p) => { await setDoc(doc(db, 'payments', p.id), cleanData({ ...p, schoolId: currentSchool?.id })); setToast({ message: 'Payment recorded', type: 'success' }); }}
              onRecordPayment={async (p) => {
                await addDoc(collection(db, 'payments'), cleanData({ ...p, schoolId: currentSchool?.id }));
                setToast({ message: 'Part payment recorded', type: 'success' });
              }}
              publishedResults={publishedResults}
              onPublishResult={async (r) => { await setDoc(doc(db, 'published_results', r.id), cleanData(r)); setToast({ message: 'Result published', type: 'success' }); }}
              onDeleteResult={async (id) => { await deleteDoc(doc(db, 'published_results', id)); setToast({ message: 'Result deleted', type: 'success' }); }}
              attendanceSubmissions={attendanceSubmissions}
              archives={archives}
              onRemoveStudentFromArchive={handleRemoveStudentFromArchive}
              iqaSubmissions={iqaSubmissions}
              onUpdateIQASubmission={handleUpdateIQASubmission}
              onUpdateAttendanceSubmission={handleUpdateAttendanceSubmission}
              announcements={announcements}
              onAddAnnouncement={async (a) => { await addDoc(collection(db, 'announcements'), cleanData({ ...a, date: Date.now() })); setToast({ message: 'Announcement added', type: 'success' }); }}
              onDeleteAnnouncement={async (id) => { await deleteDoc(doc(db, 'announcements', id)); setToast({ message: 'Announcement deleted', type: 'success' }); }}
              studentFeedback={studentFeedback}
              onUpdateStudentFeedback={async (f) => { await updateDoc(doc(db, 'student_feedback', f.id), cleanData(f)); setToast({ message: 'Feedback updated', type: 'success' }); }}
              nonAcademicStaff={nonAcademicStaff}
              onAddNonAcademicStaff={handleAddNonAcademicStaff}
              onUpdateNonAcademicStaff={handleUpdateNonAcademicStaff}
              onDeleteNonAcademicStaff={handleDeleteNonAcademicStaff}
              issueReports={issueReports}
              onUpdateIssueReport={async (r) => { await updateDoc(doc(db, 'issue_reports', r.id), cleanData(r)); setToast({ message: 'Issue updated', type: 'success' }); }}
              clockInRecords={clockInRecords}
              payrolls={payrolls}
              onAddPayroll={async (p) => { await addDoc(collection(db, 'payrolls'), cleanData(p)); setToast({ message: 'Payroll added', type: 'success' }); }}
              onUpdatePayroll={async (p) => {
                await updateDoc(doc(db, 'payrolls', p.id), cleanData(p));
                if (p.status === 'Paid') {
                  // The reports and the ledger count paid payroll directly; it is no longer copied into
                  // school expenses as well (that counted salaries twice).
                  if (!p.paidAt) {
                    await updateDoc(doc(db, 'payrolls', p.id), { paidAt: Date.now() });
                  }
                  setToast({ message: 'Payroll marked as Paid', type: 'success' });
                } else {
                  setToast({ message: 'Payroll updated', type: 'success' });
                }
              }}
              onRunPayroll={handleRunPayroll}
              salaryAdvances={salaryAdvances}
              onUpdateSalaryAdvance={handleUpdateSalaryAdvance}
              expenseClaims={expenseClaims}
              onUpdateExpenseClaim={handleUpdateExpenseClaim}
              observations={observations}
              onAddOrUpdateObservation={handleAddOrUpdateObservation}
              actionPlanItems={actionPlanItems}
              onAddActionPlanItem={async (i) => { await addDoc(collection(db, 'action_plan_items'), cleanData(i)); setToast({ message: 'Action plan item added', type: 'success' }); }}
              onUpdateActionPlanItem={async (id, updates) => { await updateDoc(doc(db, 'action_plan_items', id), cleanData(updates)); setToast({ message: 'Action plan updated', type: 'success' }); }}
              standardisationFiles={standardisationFiles}
              reviews={reviews}
              onUpdateReview={handleUpdateReview}
              onSaveReviewDraft={handleSaveReviewDraft}
              weeklyPerformances={weeklyPerformances}
              feeCategories={feeCategories}
              onAddFeeCategory={handleAddFeeCategory}
              onUpdateFeeCategory={handleUpdateFeeCategory}
              onDeleteFeeCategory={handleDeleteFeeCategory}
              feeTemplates={feeTemplates}
              onAddFeeTemplate={handleAddFeeTemplate}
              onUpdateFeeTemplate={handleUpdateFeeTemplate}
              onDeleteFeeTemplate={handleDeleteFeeTemplate}
              discounts={discounts}
              onAddDiscount={handleAddDiscount}
              onUpdateDiscount={handleUpdateDiscount}
              onDeleteDiscount={handleDeleteDiscount}
              iqaSamplingPlan={iqaSamplingPlan}
              onAddOrUpdateSamplingEntry={handleAddOrUpdateSamplingEntry}
              onDeleteSamplingEntry={handleDeleteSamplingEntry}
              standardizationMeetings={standardizationMeetings}
              admissionApplications={admissionApplications}
              tutorApplications={teacherApplications}
              onApproveApplication={handleApproveApplication}
              onRejectApplication={handleRejectApplication}
              onScheduleInterview={handleScheduleInterview}
              onDeleteApplications={handleDeleteApplications}
              onClearAllAttendance={handleClearAllAttendance}
              notifications={notifications.filter(n => n.recipientId === currentUser?.id || n.recipientId === 'admin_broadcast')}
              onRecordResit={handleRecordResit}
              onScheduleResit={handleScheduleResit}
              onApplyMissedResitConsequence={handleApplyMissedResitConsequence}
              summerPrograms={summerPrograms}
              onAddSummerProgram={handleAddSummerProgram}
              onUpdateSummerProgram={handleUpdateSummerProgram}
              onDeleteSummerProgram={handleDeleteSummerProgram}
              summerEnrollments={summerEnrollments}
              onOfferSummerEnrollment={handleOfferSummerEnrollment}
              summerProgramSignups={summerProgramSignups}
              onVerifySummerProgramSignup={handleVerifySummerProgramSignup}
            />
          </ErrorBoundary>
        );
      case Portal.TEACHER:
        const teacherAccount = currentUser?.data as TeacherAccount;
        const teacherClasses = classes.filter(c =>
          teacherAccount?.sectionIds?.includes(c.sectionId) ||
          teacherAccount?.subjects?.some(s => s.classId === c.id) ||
          c.teacherId === teacherAccount?.username ||
          c.teacherId === teacherAccount?.id ||
          c.teacherId === currentUser?.id
        );
        const teacherStudents = students.filter(s =>
          // In a section managed by teacher (checking enrollments as s.sectionId might not exist on type)
          s.enrollments?.some(e => teacherAccount?.sectionIds?.includes(e.sectionId)) ||
          // OR Enrolled in a class taught by teacher
          s.enrollments?.some(e => teacherClasses.some(c => c.id === e.classId))
        );

        // DEBUG: Teacher Finance
        console.log("DEBUG: Current User ID:", currentUser?.id);
        console.log("DEBUG: All Payrolls:", payrolls.length, payrolls);
        console.log("DEBUG: Filtered Payrolls (teacherId):", payrolls.filter(p => p.teacherId === currentUser?.id));
        console.log("DEBUG: Filtered Payrolls (staffId - CHECK):", payrolls.filter(p => (p as any).staffId === currentUser?.id));

        return (
          <ErrorBoundary>
            <TeacherPortal
              user={currentUser!}
              teacherAccount={teacherAccount}
              onLogout={handleLogout}
              unreadMessageCount={unreadMessageCount}
              onAddReview={handleAddReview}
              teacherReviews={reviews.filter(r => r.teacher === currentUser?.id)}
              publishedResults={publishedResults}
              assignedClasses={teacherClasses}
              allClasses={classes}
              assignedStudents={teacherStudents}
              sections={sections}
              iqaSubmissions={iqaSubmissions.filter(s => s.teacherId === currentUser?.id)}
              onAddIQASubmission={handleAddIQASubmission}
              onUpdateTeacherAccount={async (t) => { await updateDoc(doc(db, 'teachers', t.username), cleanData(t)); setToast({ message: 'Profile updated', type: 'success' }); }}
              onUpdateStudent={async (s) => { await updateDoc(doc(db, 'students', s.id), cleanData(s)); setToast({ message: 'Student updated', type: 'success' }); }}
              attendanceSubmissions={attendanceSubmissions}
              onAddAttendanceSubmission={handleAddAttendanceSubmission}
              announcements={announcements}
              schoolSettings={schoolSettings}
              teacherPayrolls={payrolls.filter(p => p.teacherId.trim().toLowerCase() === currentUser?.id?.trim().toLowerCase() || (p as any).staffId?.trim().toLowerCase() === currentUser?.id?.trim().toLowerCase())}
              teacherExpenseClaims={expenseClaims.filter(c => c.teacherId.trim().toLowerCase() === currentUser?.id?.trim().toLowerCase() || (c as any).staffId?.trim().toLowerCase() === currentUser?.id?.trim().toLowerCase())}
              onAddExpenseClaim={handleAddExpenseClaim}
              teacherSalaryAdvances={salaryAdvances.filter(r => r.teacherId.toLowerCase() === currentUser?.id.toLowerCase() || (r as any).staffId?.toLowerCase() === currentUser?.id.toLowerCase())}
              onAddSalaryAdvance={handleAddSalaryAdvance}
              teacherObservations={observations.filter(o => o.teacherId === currentUser?.id)}
              teacherActionPlanItems={actionPlanItems.filter(i => i.teacherId === currentUser?.id)}
              onUpdateActionPlanItem={async (id, updates) => { await updateDoc(doc(db, 'action_plan_items', id), cleanData(updates)); setToast({ message: 'Action plan updated', type: 'success' }); }}
              standardisationFiles={standardisationFiles}
              standardizationMeetings={standardizationMeetings}
              onAddStandardisationFile={async (f) => { await addDoc(collection(db, 'standardisation_files'), cleanData(f)); setToast({ message: 'File uploaded', type: 'success' }); }}
              cbtTests={cbtTests.filter(t => t.teacherId === currentUser?.id)}
              onAddCBTTest={async (t) => { await addDoc(collection(db, 'cbt_tests'), cleanData(t)); setToast({ message: 'Test created', type: 'success' }); }}
              onUpdateCBTTest={async (id, updates) => { await updateDoc(doc(db, 'cbt_tests', id), cleanData(updates)); setToast({ message: 'Test updated', type: 'success' }); }}
              onDeleteCBTTest={async (id) => { await deleteDoc(doc(db, 'cbt_tests', id)); setToast({ message: 'Test deleted', type: 'success' }); }}
              clockInRecords={clockInRecords}
              onClockInOut={() => handleClockInOut(currentUser!.id)}
              loading={loading}
              onDeleteReview={async (id) => { await deleteDoc(doc(db, 'reviews', id)); setToast({ message: 'Review deleted', type: 'success' }); }}
              notifications={notifications.filter(n => n.recipientId === currentUser?.id)}
              onRecordResit={handleRecordResit}
              onScheduleResit={handleScheduleResit}
            />
          </ErrorBoundary>
        );
      case Portal.STUDENT:
        let student = liveStudentDoc || students.find(s => s.email === currentUser?.email || s.reg === currentUser?.email) || (currentUser?.data as Student);

        // Inject className for Analytics helper (Runtime injection)
        if (student && classes.length > 0 && student.enrollments && student.enrollments.length > 0) {
          // Find enrollment for current session if possible, or just the first one
          // Assuming active class is relevant.
          // We can try to find the class that has the same ID as the enrollment
          const enrolledClass = classes.find(c => student.enrollments.some(e => e.classId === c.id));
          if (enrolledClass) {
            student = { ...student, className: enrolledClass.name } as any;
          }
        }

        return (
          <Suspense fallback={<div className="h-screen flex items-center justify-center"><Spinner /></div>}>
            <ErrorBoundary>
              <StudentPortal
                user={currentUser!}
                student={student}
                onLogout={handleLogout}
                unreadMessageCount={unreadMessageCount}
                studentResults={student ? publishedResults.filter(r => matchesPublishedResult(r, student, classes, sections)) : []}
                allPublishedResults={publishedResults}
                loading={loading}
                allAttendanceSubmissions={attendanceSubmissions}
                allIqaSubmissions={iqaSubmissions}
                classes={classes}
                sections={sections}
                announcements={announcements}
                invoices={invoices.filter(i => i.studentId === student?.id)}
                payments={payments.filter(p => p.studentId === student?.id)}
                schoolSettings={schoolSettings}
                onUploadProofOfPayment={handleUploadProofOfPayment}
                studentFeedback={studentFeedback.filter(f => f.studentId === student?.id)}
                onAddStudentFeedback={handleAddStudentFeedback}
                cbtTests={cbtTests}
                notifications={notifications.filter(n =>
                  n.recipientId === student?.id ||
                  n.recipientId === 'all' ||
                  (n.targetClassId && student?.enrollments.some(e => e.classId === n.targetClassId))
                )}
                currentSchool={currentSchool!}
                teachers={teachers.map(t => ({ id: t.username, fullName: t.fullName }))}
                summerEnrollments={summerEnrollments.filter(e => e.studentId === student?.id)}
                onRespondSummerEnrollment={handleRespondSummerEnrollment}
                feeTemplates={feeTemplates}
                feeCategories={feeCategories}
                onGenerateInvoices={handleGenerateInvoices}
                onDropEnrollment={handleDropEnrollment}
                transferRequests={transferRequests}
                onSubmitTransferRequest={handleSubmitTransferRequest}
                onCancelTransferRequest={handleCancelTransferRequest}
              />
            </ErrorBoundary>
          </Suspense>
        );
      case Portal.EXTERNAL_STUDENT: {
        const extStudent = students.find(s => s.email === currentUser?.email || s.reg === currentUser?.email);
        return (
          <Suspense fallback={<div className="h-screen flex items-center justify-center"><Spinner /></div>}>
            <ExternalStudentPortal
              user={currentUser!}
              student={extStudent}
              onLogout={handleLogout}
              studentResults={publishedResults.filter(r => r.studentId === extStudent?.id)}
              allPublishedResults={publishedResults}
              allAttendanceSubmissions={attendanceSubmissions}
              iqaSubmissions={iqaSubmissions}
              announcements={announcements}
              schoolSettings={schoolSettings}
              invoices={invoices.filter(i => i.studentId === extStudent?.id)}
              payments={payments.filter(p => p.studentId === extStudent?.id)}
              onUploadProofOfPayment={handleUploadProofOfPayment}
              studentFeedback={studentFeedback.filter(f => f.studentId === extStudent?.id)}
              onAddStudentFeedback={handleAddStudentFeedback}
              cbtTests={cbtTests}
              classes={classes}
              loading={loading}
              allIqaSubmissions={iqaSubmissions}
            />
          </Suspense>
        );
      }
      case Portal.NON_ACADEMIC:
        // DEBUG: Non-Academic Finance - Case Insensitive Check
        console.log("DEBUG: Non-Academic User ID:", currentUser?.id);
        console.log("DEBUG: All Payrolls:", payrolls.length);
        console.log("DEBUG: Filtered Payrolls:", payrolls.filter(p => p.teacherId.toLowerCase() === currentUser?.id.toLowerCase() || (p as any).staffId?.toLowerCase() === currentUser?.id.toLowerCase()));

        return (
          <ErrorBoundary>
            <NonAcademicPortal
              user={currentUser!}
              staffAccount={currentUser!.data as NonAcademicStaffAccount}
              onLogout={handleLogout}
              onUpdateAccount={async (p) => { await updateDoc(doc(db, 'non_academic_staff', p.username), cleanData(p)); setToast({ message: 'Profile updated', type: 'success' }); }}
              announcements={announcements}
              schoolSettings={schoolSettings}
              onAddIssueReport={handleAddIssueReport}
              issueReports={issueReports.filter(r => r.reportedById === currentUser?.id || r.assignedToId === currentUser?.id)}
              clockInRecords={clockInRecords.filter(r => r.staffId === currentUser?.id)}
              onClockInOut={() => handleClockInOut(currentUser!.id)}
              payrolls={payrolls.filter(p => p.teacherId.toLowerCase() === currentUser?.id.toLowerCase() || (p as any).staffId?.toLowerCase() === currentUser?.id.toLowerCase())}
              salaryAdvances={salaryAdvances.filter(r => r.teacherId.toLowerCase() === currentUser?.id.toLowerCase() || (r as any).staffId?.toLowerCase() === currentUser?.id.toLowerCase())}
              onAddSalaryAdvance={handleAddSalaryAdvance}
            />
          </ErrorBoundary>
        );
      case Portal.EQA:
        return <EQAPortal
          onLogout={() => setActivePortal(Portal.LANDING)}
          schoolSettings={schoolSettings}
          publishedResults={publishedResults}
          iqaSubmissions={iqaSubmissions}
          attendanceSubmissions={attendanceSubmissions}
          observations={observations}
          actionPlanItems={actionPlanItems}
          standardisationFiles={standardisationFiles}
          reviews={reviews}
          teachers={teachers}
          students={students}
        />;
      case Portal.CBT_STUDENT:
        // This portal state is usually triggered from StudentPortal, but if we have a direct route or state:
        // We need a way to pass the testId. For now, assuming it's handled via state or props if we were using a router.
        // Since we are using conditional rendering, we might need a separate state for 'activeTestId' in App.
        return <div>CBT Student View (Please access via Student Portal)</div>;
      case Portal.CBT_TEACHER:
        return <div>CBT Teacher View (Please access via Teacher Portal)</div>;
      case Portal.LANDING:
      default:
        return (
          <ErrorBoundary>
            <LandingPage onLogin={handleLogin} schoolSettings={schoolSettings} loading={loading || schoolLoading} />
          </ErrorBoundary>
        );
    }
  };

  return (
    <>
      <Suspense fallback={<div className="flex items-center justify-center h-screen bg-slate-900"><Spinner size="lg" /></div>}>
        <SubscriptionGate bypass={activePortal === Portal.LANDING} onSignOut={handleLogout} role={currentUser?.role}>
          {renderPortal()}
        </SubscriptionGate>
        {currentUser && activePortal !== Portal.LANDING && <DemoBanner key={currentUser.id || currentUser.email} />}
      </Suspense>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
};



const ExternalJudgeView = lazyRetry(() => import('./components/portals/musabaqa/ExternalJudgeView'));
const MusabaqaShortLink = lazyRetry(() => import('./components/portals/musabaqa/MusabaqaShortLink'));

const AppWrapper: React.FC = () => {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/material/:id" element={<SharedMaterialView />} />
          <Route path="/quiz/:id" element={<PublicQuizView />} />
          <Route path="/poll/:id" element={<PublicQuizView />} />
          <Route path="/activity/:id" element={
            <Suspense fallback={<div className="h-screen flex items-center justify-center"><Spinner /></div>}>
              <PublicActivityView />
            </Suspense>
          } />
          <Route path="/enroll/:courseId" element={
            <Suspense fallback={<div className="h-screen flex items-center justify-center"><Spinner /></div>}>
              <PublicEnrollment />
            </Suspense>
          } />
          <Route path="/apply" element={
            <Suspense fallback={<div className="h-screen flex items-center justify-center bg-slate-900"><div className="w-10 h-10 rounded-full border-4 border-violet-500 border-t-transparent animate-spin" /></div>}>
              <PublicAdmissionForm />
            </Suspense>
          } />
          <Route path="/summer-signup" element={
            <Suspense fallback={<div className="h-screen flex items-center justify-center bg-slate-900"><div className="w-10 h-10 rounded-full border-4 border-amber-500 border-t-transparent animate-spin" /></div>}>
              <PublicSummerProgramForm />
            </Suspense>
          } />
          <Route path="/musabaqa/judge" element={
            <Suspense fallback={<div className="h-screen flex items-center justify-center bg-[#0a0f1e]"><div className="w-10 h-10 rounded-full border-4 border-emerald-400 border-t-transparent animate-spin" /></div>}>
              <ExternalJudgeView />
            </Suspense>
          } />
          <Route path="/m/:code" element={
            <Suspense fallback={<div className="h-screen flex items-center justify-center bg-[#0a0f1e]"><div className="w-10 h-10 rounded-full border-4 border-emerald-400 border-t-transparent animate-spin" /></div>}>
              <MusabaqaShortLink />
            </Suspense>
          } />
          <Route path="/musabaqa/broadcast" element={
            <Suspense fallback={<div style={{ width: 1280, height: 720, background: '#06261d' }} />}>
              <BroadcastView />
            </Suspense>
          } />
          <Route path="/musabaqa/sidecam" element={
            <Suspense fallback={<div className="h-screen bg-slate-950" />}>
              <SideCamera />
            </Suspense>
          } />
          <Route path="/join/:sessionId" element={
            <Suspense fallback={<div className="h-screen flex items-center justify-center bg-slate-900"><div className="w-10 h-10 rounded-full border-4 border-emerald-400 border-t-transparent animate-spin" /></div>}>
              <GuestClassroomView />
            </Suspense>
          } />

          <Route path="*" element={<App />} />
        </Routes>
      </BrowserRouter>
    </LanguageProvider>
  );
};

export default AppWrapper;
